import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Printer, X, History, Calendar } from "lucide-react";
import Layout from "./layout";
import TemplateDetailModal from "../Modal/TemplateDetailModal";
import EditTemplateModal from "../Modal/editTamplateModal";
import HealthResultModal from "../Modal/HealthResultModal";
import SendHealthResultModal from "../Modal/SendHealthResultModal";
import HealthReportModal from "../Modal/HealthReportModal";
import HealthRecordsHistory from "./HealthRecordsHistory";
import { useNavigate } from "react-router-dom";
import { getUnifiedParameters } from "../utils/healthParameterNormalizer";
import {
  healthRecordsAPI,
  templateAPI,
  normalRangeAPI,
  healthRecommendationAPI,
  memberAPI,
  referenceDataAPI,
  campaignAPI,
} from "../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createGlobalStyle } from "styled-components";

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.split("T")[0].split(" ")[0];
  let parts = null;

  if (dateOnly.includes("-")) {
    parts = dateOnly.split("-");
  } else if (dateOnly.includes("/")) {
    parts = dateOnly.split("/");
  }

  if (parts?.length === 3) {
    let year;
    let month;
    let day;

    if (parts[0].length === 4) {
      year = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      day = Number.parseInt(parts[2], 10);
    } else {
      day = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      year = Number.parseInt(parts[2], 10);
    }

    if (year > 2400) {
      year -= 543;
    } else if (year < 100) {
      year += 2000;
    }

    const parsed = new Date(year, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const calculateAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) {
    return null;
  }

  let age = examDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = examDate.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && examDate.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  if (age < 0 || age > 150) {
    return null;
  }

  return age;
};

// อายุแบบละเอียด (ทศนิยมของปี) สำหรับจับคู่ช่วงอายุใน medium.json ที่มีหน่วยเป็นวัน/เดือน (เช่น ทารกแรกเกิด)
const calculatePreciseAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) {
    return null;
  }

  const msPerDay = 24 * 60 * 60 * 1000;
  const ageInYears =
    (examDate.getTime() - birthDate.getTime()) / msPerDay / 365.25;

  if (ageInYears < 0 || ageInYears > 150) {
    return null;
  }

  return ageInYears;
};

// Custom styles สำหรับ DatePicker
const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: inherit;
    border: 2px solid #00B5B8;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .react-datepicker__header {
    background-color: #00B5B8;
    border-bottom: none;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1rem 0.75rem 0.75rem;
  }

  .react-datepicker__current-month {
    color: #FFFFFF;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: #FFFFFF;
    font-size: 0.875rem;
    font-weight: 600;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
  }

  .react-datepicker__day {
    color: #1F2937;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.375rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
    transition: all 0.2s;

    &:hover {
      background-color: #00B5B8;
      color: white;
      transform: scale(1.1);
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 700;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #008F92;
      border-color: #008F92;
    }
  }

  .react-datepicker__day--disabled {
    color: #D1D5DB;
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
      transform: none;
    }
  }

  .react-datepicker__day--today {
    font-weight: 700;
    color: #00B5B8;
    background-color: #E0F2F3;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #00B5B8;
      color: white;
    }
  }

  .react-datepicker__day--outside-month {
    color: #9CA3AF;
    opacity: 0.5;
  }

  .react-datepicker__month-dropdown,
  .react-datepicker__year-dropdown {
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__month-option,
  .react-datepicker__year-option {
    font-size: 0.875rem;
    padding: 0.5rem;

    &:hover {
      background-color: #E0F2F3;
      color: #00B5B8;
    }
  }

  .react-datepicker__month-option--selected,
  .react-datepicker__year-option--selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 600;
  }

  .react-datepicker__navigation {
    top: 1rem;
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
    border-width: 2px 2px 0 0;
    height: 7px;
    width: 7px;
  }

  .react-datepicker__month-container {
    padding: 0.5rem;
  }
`;

const PageContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr auto auto;
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const GridItem = styled.div`
  grid-column: ${(props) => props.column};
  grid-row: ${(props) => props.row};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const DateInputContainer = styled.div`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0891b2;
  }
`;

const PrintButton = styled.button`
  background-color: #f9fafb;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const TemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${(props) => (props.disabled ? "#F9FAFB" : "transparent")};
  border: 1px solid ${(props) => (props.disabled ? "#D1D5DB" : "#00B5B8")};
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#00B5B8")};
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  min-height: 30px;
  height: auto;
  width: auto;
  position: relative;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const TemplateBadgeText = styled.span`
  margin-right: 0.5rem;
`;

const TagBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid #6ee7b7;
  border-radius: 0.375rem;
  padding: 0.25rem 0.75rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: auto;
  width: auto;
  font-weight: 500;
`;

const EditButton = styled.button`
  background-color: ${(props) => (props.disabled ? "#F3F4F6" : "#FFFFFF")};
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#374151")};
  border: 1px solid ${(props) => (props.disabled ? "#E5E7EB" : "#CFD3D4")};
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  margin: 0.125rem;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#F3F4F6" : "#F9FAFB")};
  }
`;

const RemoveButton = styled.button`
  background: ${(props) => (props.disabled ? "#F3F4F6" : "white")};
  border: 1px solid ${(props) => (props.disabled ? "#D1D5DB" : "#00B5B8")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#00B5B8")};
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: auto;

  &:hover {
    background: ${(props) => (props.disabled ? "#F3F4F6" : "#F9FAFB")};
  }
`;

const ViewDataButton = styled.button`
  background-color: #fbffff;
  color: #9c9e9eff;
  border: 1px solid #cfd3d4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  min-height: 24px;
  color: ${(props) =>
    props.$status === "รอการอนุมัติ"
      ? "#FF0404"
      : props.$status === "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
        ? "#00B5B8"
        : props.$status === "มีการแก้ไขรอการอนุมัติอีกครั้ง"
          ? "#FF0404"
          : "#6F6F6F"};

  /* 🔍 DEBUG: Force visibility */
  opacity: 1 !important;
  visibility: visible !important;
`;

const ActionPrintButton = styled.button`
  background: none;
  color: ${(props) => (props.disabled ? "#9CA3AF" : "#374151")};
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    color: ${(props) => (props.disabled ? "#9CA3AF" : "#00B5B8")};
  }
`;

const ViewHiddenButton = styled.button`
  background-color: #fff7ed;
  color: #ea580c;
  border: 1px solid #fdba74;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #ffedd5;
    border-color: #fb923c;
  }
`;

const SendResultButton = styled.button`
  background-color: #00b5b8;
  color: #ffffff;
  border: 1px solid #00b5b8;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #0891b2;
    border-color: #0891b2;
  }
`;

const ResendButton = styled.button`
  background-color: #1d87f0;
  color: #ffffff;
  border: 1px solid #1d87f0;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #1565c0;
    border-color: #1565c0;
  }
`;

const HistoryButton = styled.button`
  background: none;
  border: none;
  color: #6f6f6f;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;

  &:hover {
    color: #6f6f6f;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1.5rem;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #00b5b8;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #a3a3a3;
  text-align: center;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #a3a3a3;
  text-align: center;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6f6f6f;
  text-align: center;
  vertical-align: top;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #00b5b8;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  background-color: transparent;
  color: ${(props) => {
    if (props.status === "high") return "#DC2626";
    if (props.status === "low") return "#D97706";
    return "#059669";
  }};
`;

// Pagination Styled Components
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  background-color: ${(props) => (props.active ? "#00B5B8" : "#FFFFFF")};
  color: ${(props) => (props.active ? "#FFFFFF" : "#374151")};
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.active ? "#0891B2" : "#F9FAFB")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageEllipsis = styled.span`
  padding: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const HealthBook = () => {
  const defaultEndDate = new Date();
  const defaultStartDate = new Date(defaultEndDate);
  defaultStartDate.setDate(defaultEndDate.getDate() - 7);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [searchCompany, setSearchCompany] = useState("");
  const [searchText, setSearchText] = useState("");
  const [activeSearchText, setActiveSearchText] = useState(""); // ใช้สำหรับ filter เฉพาะเมื่อกดค้นหา
  const [totalPages, setTotalPages] = useState(0); // จำนวนหน้าทั้งหมดจาก API
  const [pageSize, setPageSize] = useState(10); // จำนวน records ต่อหน้าจาก API
  const [status, setStatus] = useState("");
  const [companies, setCompanies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHealthResultModalOpen, setIsHealthResultModalOpen] = useState(false);
  const [isSendHealthResultModalOpen, setIsSendHealthResultModalOpen] =
    useState(false);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedReportRecord, setSelectedReportRecord] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historySelectedRecord, setHistorySelectedRecord] = useState(null);
  const [isHiddenTemplatesModalOpen, setIsHiddenTemplatesModalOpen] =
    useState(false);
  const [hiddenTemplates, setHiddenTemplates] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const currentFiltersRef = useRef({}); // เก็บ filters ปัจจุบันสำหรับ page navigation

  // Checkbox selection state
  const [selectedRecords, setSelectedRecords] = useState([]);

  // Alert Modal state
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

  const navigate = useNavigate();

  // ป้องกัน race condition: fetch เก่าไม่ overwrite ผลค้นหาใหม่
  const fetchIdRef = useRef(0);
  const activeSearchTextRef = useRef(""); // ref สำหรับ filter ใน useEffect เพื่อหลีกเลี่ยง timing issue
  const hasSearchedRef = useRef(false); // ref สำหรับเช็คว่าเคย search แล้วหรือยัง

  // แปลง Date object เป็น YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateStopToYYYYMMDD = (date) => {
    return formatDateToYYYYMMDD(date);
  };

  const normalizeGenderText = (gender) => {
    const value = String(gender || "")
      .trim()
      .toUpperCase();
    if (value === "M" || value === "MALE" || value === "ชาย" || value === "1")
      return "ชาย";
    if (
      value === "F" ||
      value === "FEMALE" ||
      value === "หญิง" ||
      value === "2"
    )
      return "หญิง";
    return gender || "-";
  };

  const normalizeApprovalStatus = (status) => {
    const value = String(status || "")
      .trim()
      .toLowerCase();
    if (value === "approved" || value === "revised") {
      return value;
    }
    return "pending";
  };

  const isApprovedStatus = (status) =>
    ["approved", "revised"].includes(normalizeApprovalStatus(status));

  const getFirstDefined = (...values) => {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return null;
  };

  const buildBloodPressure = (sourceRecord = {}, healthData = {}) => {
    const systolic = getFirstDefined(
      healthData?.bps,
      healthData?.sys,
      sourceRecord?.bps,
      sourceRecord?.sys,
      sourceRecord?.systolic_bp,
      healthData?.bloodPressure?.split?.("/")?.[0]?.trim(),
    );
    const diastolic = getFirstDefined(
      healthData?.bpd,
      healthData?.dia,
      sourceRecord?.bpd,
      sourceRecord?.dia,
      sourceRecord?.diastolic_bp,
      healthData?.bloodPressure?.split?.("/")?.[1]?.trim(),
    );

    if (systolic && diastolic) {
      return {
        bloodPressure: `${systolic}/${diastolic}`,
        bps: systolic,
        bpd: diastolic,
      };
    }

    const existingBloodPressure = getFirstDefined(
      healthData?.bloodPressure,
      sourceRecord?.bloodPressure,
      sourceRecord?.sys_dias,
      healthData?.["sys/dias"],
    );

    if (existingBloodPressure) {
      const [sys, dia] = String(existingBloodPressure)
        .split("/")
        .map((part) => part.trim());
      return {
        bloodPressure: existingBloodPressure,
        bps: sys || "-",
        bpd: dia || "-",
      };
    }

    return {
      bloodPressure: "-",
      bps: "-",
      bpd: "-",
    };
  };

  const parseObjectValue = (value) => {
    if (!value) return {};

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
          ? parsed
          : {};
      } catch (error) {
        return {};
      }
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      return value;
    }

    return {};
  };

  const pickFirstArray = (...candidates) => {
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate;
      }
    }
    return [];
  };

  const mergeReportSourceRecord = (sourceRecord, detailRecord = null) => {
    const safeSourceRecord = parseObjectValue(sourceRecord);
    const safeDetailRecord = parseObjectValue(detailRecord);
    const sourceHealthData = parseObjectValue(safeSourceRecord?.health_data);
    const detailHealthData = parseObjectValue(safeDetailRecord?.health_data);
    const detailLabData = parseObjectValue(safeDetailRecord?.labData);
    const sourceLabData = parseObjectValue(safeSourceRecord?.labData);
    const hasDetailPayload =
      Object.keys(detailHealthData).length > 0 ||
      Object.keys(detailLabData).length > 0 ||
      Array.isArray(safeDetailRecord?.parameters) ||
      Array.isArray(safeDetailRecord?.lab_results) ||
      Array.isArray(detailHealthData?.parameters) ||
      Array.isArray(detailHealthData?.lab_results);

    const mergedHealthData = hasDetailPayload
      ? detailHealthData
      : sourceHealthData;

    const mergedLabData = {
      ...(hasDetailPayload ? detailLabData : sourceLabData),
      ...(hasDetailPayload
        ? parseObjectValue(detailHealthData?.labData)
        : parseObjectValue(sourceHealthData?.labData)),
    };

    const mergedParameters = hasDetailPayload
      ? pickFirstArray(
          detailHealthData?.parameters,
          safeDetailRecord?.parameters,
        )
      : pickFirstArray(
          detailHealthData?.parameters,
          safeDetailRecord?.parameters,
          sourceHealthData?.parameters,
          safeSourceRecord?.parameters,
        );
    const mergedLabResults = hasDetailPayload
      ? pickFirstArray(
          detailHealthData?.lab_results,
          safeDetailRecord?.lab_results,
        )
      : pickFirstArray(
          detailHealthData?.lab_results,
          safeDetailRecord?.lab_results,
          sourceHealthData?.lab_results,
          safeSourceRecord?.lab_results,
        );
    const mergedHealthDataList = hasDetailPayload
      ? pickFirstArray(
          detailHealthData?.health_data,
          safeDetailRecord?.health_data,
        )
      : pickFirstArray(
          detailHealthData?.health_data,
          safeDetailRecord?.health_data,
          sourceHealthData?.health_data,
          safeSourceRecord?.health_data,
        );

    return {
      ...safeSourceRecord,
      ...safeDetailRecord,
      health_data: {
        ...mergedHealthData,
        parameters: mergedParameters,
        lab_results: mergedLabResults,
        health_data: mergedHealthDataList,
        labData: mergedLabData,
      },
      parameters: mergedParameters,
      lab_results: mergedLabResults,
      labData: mergedLabData,
    };
  };

  const normalizeReportRecord = (
    sourceRecord,
    detailRecord = null,
    recommendationRow = null,
  ) => {
    const mergedRecord = mergeReportSourceRecord(sourceRecord, detailRecord);
    const healthData = parseObjectValue(mergedRecord?.health_data);
    const approved = isApprovedStatus(recommendationRow?.approval_status);
    const approvalStatus = approved
      ? normalizeApprovalStatus(recommendationRow?.approval_status)
      : normalizeApprovalStatus(mergedRecord?.approvalStatus);
    const bloodPressureData = buildBloodPressure(mergedRecord, healthData);
    const recommendationPayload = approved
      ? {
          approvalStatus,
          approvedAt:
            recommendationRow?.approved_at || mergedRecord?.approvedAt || null,
          approvedBy:
            recommendationRow?.approved_by || mergedRecord?.approvedBy || null,
          doctorNote:
            recommendationRow?.doctor_advice ||
            mergedRecord?.doctorNote ||
            mergedRecord?.doctor_advice ||
            "-",
          nutritionAdvice: recommendationRow?.nutrition_advice || "",
          exerciseAdvice: recommendationRow?.exercise_advice || "",
          sleepAdvice: recommendationRow?.sleep_advice || "",
        }
      : {
          approvalStatus,
          approvedAt: mergedRecord?.approvedAt || null,
          approvedBy: mergedRecord?.approvedBy || null,
          doctorNote:
            mergedRecord?.doctorNote || mergedRecord?.doctor_advice || "-",
          nutritionAdvice: "",
          exerciseAdvice: "",
          sleepAdvice: "",
        };

    const parameters = getUnifiedParameters(mergedRecord, masterData);
    const filteredReportParameters = parameters;
    const sourceParameters = parameters;

    const labData = mergedRecord?.labData || {};

    const matchedTemplates = Array.isArray(mergedRecord?.matchedTemplates)
      ? mergedRecord.matchedTemplates
      : [];

    const matchedTemplateNames = matchedTemplates
      .map((template) => template?.template_name)
      .filter(Boolean);
    const templateNames =
      Array.isArray(mergedRecord?.templates) &&
      mergedRecord.templates.length > 0
        ? mergedRecord.templates
        : matchedTemplateNames;

    const firstName = mergedRecord?.firstname;
    const lastName = mergedRecord?.lastname;

    return {
      ...mergedRecord,
      id:
        mergedRecord?.id ||
        detailRecord?.id ||
        mergedRecord?.dbRecordId ||
        null,
      dbRecordId:
        detailRecord?.id ||
        mergedRecord?.dbRecordId ||
        mergedRecord?.id ||
        null,
      patientName:
        healthData?.patientName ||
        mergedRecord?.patientName ||
        `${firstName || ""} ${lastName || ""}`.trim() ||
        "-",
      age:
        calculateAgeAtDate(
          healthData?.birthday ||
            healthData?.birthdate ||
            healthData?.birth_date ||
            mergedRecord?.birthday ||
            mergedRecord?.birthdate ||
            mergedRecord?.birth_date ||
            detailRecord?.birthday ||
            detailRecord?.birthdate ||
            detailRecord?.birth_date,
          healthData?.rawDate ||
            mergedRecord?.rawDate ||
            mergedRecord?.dateadd ||
            healthData?.date ||
            mergedRecord?.date ||
            detailRecord?.visit_date,
        ) ||
        healthData?.age ||
        mergedRecord?.age ||
        "-",
      gender: normalizeGenderText(
        healthData?.gender || healthData?.sex || mergedRecord?.gender,
      ),
      hn: healthData?.hn || mergedRecord?.hn || detailRecord?.hn || "-",
      date:
        healthData?.date ||
        mergedRecord?.date ||
        detailRecord?.visit_date ||
        "-",
      rawDate:
        healthData?.rawDate ||
        mergedRecord?.rawDate ||
        mergedRecord?.dateadd ||
        "",
      birthday:
        healthData?.birthday ||
        healthData?.birthdate ||
        healthData?.birth_date ||
        mergedRecord?.birthday ||
        mergedRecord?.birthdate ||
        mergedRecord?.birth_date ||
        detailRecord?.birthday ||
        detailRecord?.birthdate ||
        detailRecord?.birth_date ||
        "",
      company:
        healthData?.company ||
        healthData?.campaign ||
        mergedRecord?.company ||
        mergedRecord?.campaign ||
        detailRecord?.campaignname ||
        "-",
      diseases:
        healthData?.diseases ||
        mergedRecord?.diseases ||
        detailRecord?.diseases ||
        "ไม่มี",
      foodAllergies:
        healthData?.foodAllergies ||
        mergedRecord?.foodAllergies ||
        labData?.food_allergies ||
        labData?.["food allergies"] ||
        "-",
      parameters: filteredReportParameters,
      sourceParameters,
      labData,
      atypicalLymphocyte: "-",
      atypicalLymphocyteNormal: "-",
      atypicalLymphocyteUnit: "-",
      templates: templateNames,
      matchedTemplates,
      health_risk_tags:
        templateNames.length > 0 ? templateNames : matchedTemplateNames,
      tags: templateNames.length > 0 ? templateNames : matchedTemplateNames,
      weight:
        healthData?.weight || mergedRecord?.weight || labData?.weight || "-",
      height:
        healthData?.height || mergedRecord?.height || labData?.height || "-",
      bmi: healthData?.bmi || mergedRecord?.bmi || labData?.bmi || "-",
      ...bloodPressureData,
      pulse: healthData?.pulse || mergedRecord?.pulse || labData?.pulse || "-",
      ...recommendationPayload,
    };
  };

  const fetchReportRecord = async (record) => {
    if (!record) return null;

    const rawRecordId = record.dbRecordId || record.id;
    const recordId =
      typeof rawRecordId === "string" ? Number(rawRecordId) : rawRecordId;
    if (!recordId || Number.isNaN(recordId)) {
      return normalizeReportRecord(record, null, null);
    }

    try {
      const [detailResponse, recommendationResponse] = await Promise.all([
        healthRecordsAPI.getById(recordId),
        healthRecommendationAPI.getDraft(recordId),
      ]);

      const detailRecord = detailResponse?.data || null;
      const recommendationRow = recommendationResponse?.data || null;
      const approvedRecommendation = isApprovedStatus(
        recommendationRow?.approval_status,
      )
        ? recommendationRow
        : null;

      return normalizeReportRecord(
        record,
        detailRecord,
        approvedRecommendation,
      );
    } catch (error) {
      console.error("Error loading full report record:", error);
      return normalizeReportRecord(record, null, null);
    }
  };

  const fetchReportRecords = async (records = []) => {
    const safeRecords = Array.isArray(records) ? records : [];
    const enrichedRecords = await Promise.all(
      safeRecords.map((record) => fetchReportRecord(record)),
    );

    return enrichedRecords.filter(Boolean);
  };

  const handleSearch = async () => {
    const trimmedSearch = searchText.trim();

    try {
      setLoading(true);
      const filters = {};
      if (startDate) filters.dateStart = formatDateToYYYYMMDD(startDate);
      if (endDate) filters.dateStop = formatDateStopToYYYYMMDD(endDate);
      if (trimmedSearch) filters.keywordName = trimmedSearch;
      if (searchCompany) filters.keywordCampaign = searchCompany;
      if (status) filters.statusFilter = status; // ส่ง status เข้า fetchHealthRecords

      activeSearchTextRef.current = "";
      setActiveSearchText("");
      hasSearchedRef.current = true;

      await fetchHealthRecords(filters);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = async (e) => {
    const company = e.target.value;
    setSearchCompany(company);
    setHealthRecords([]);
    try {
      setLoading(true);
      const filters = {};
      if (startDate) filters.dateStart = formatDateToYYYYMMDD(startDate);
      if (endDate) filters.dateStop = formatDateStopToYYYYMMDD(endDate);
      if (searchText.trim()) filters.keywordName = searchText.trim();
      if (company) filters.keywordCampaign = company;
      if (status) filters.statusFilter = status;
      activeSearchTextRef.current = "";
      setActiveSearchText("");
      hasSearchedRef.current = true;
      await fetchHealthRecords(filters);
    } catch (error) {
      console.error("Error filtering by company:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (recordId) => {
    // เปิด HealthReportModal หลังจากดึง record เต็มจาก backend แล้ว
    const record = healthRecords.find((r) => r.id === recordId);
    if (!record) return;

    const reportRecord = await fetchReportRecord(record);
    if (reportRecord) {
      setSelectedReportRecord(reportRecord);
      setIsHealthReportModalOpen(true);
    }
  };

  // จัดการ checkbox selection
  const handleCheckboxChange = (recordId) => {
    setSelectedRecords((prev) => {
      if (prev.includes(recordId)) {
        // ถ้าเลือกอยู่แล้ว ให้ยกเลิก
        return prev.filter((id) => id !== recordId);
      } else {
        // ถ้ายังไม่ได้เลือก ให้เพิ่ม (สูงสุด 10 คน)
        if (prev.length >= 10) {
          alert("สามารถเลือกได้สูงสุด 10 คนเท่านั้น");
          return prev;
        }
        return [...prev, recordId];
      }
    });
  };

  // จัดการ checkbox ทั้งหมด
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageRecords = healthRecords.map((record) => record.id);
      const newSelection = [
        ...new Set([...selectedRecords, ...currentPageRecords]),
      ];
      if (newSelection.length > 10) {
        alert("สามารถเลือกได้สูงสุด 10 คนเท่านั้น");
        setSelectedRecords(newSelection.slice(0, 10));
      } else {
        setSelectedRecords(newSelection);
      }
    } else {
      const currentPageRecords = healthRecords.map((record) => record.id);
      setSelectedRecords((prev) =>
        prev.filter((id) => !currentPageRecords.includes(id)),
      );
    }
  };

  // จัดการ batch print - รวมเป็นเอกสารเดียว
  const handleBatchPrint = async () => {
    if (selectedRecords.length === 0) {
      setIsAlertModalOpen(true);
      return;
    }

    if (selectedRecords.length > 10) {
      alert("สามารถพิมพ์ได้สูงสุด 10 คนเท่านั้น");
      return;
    }

    // รวม records ที่เลือกทั้งหมด
    const recordsToPrint = selectedRecords
      .map((recordId) => healthRecords.find((r) => r.id === recordId))
      .filter((record) => record !== undefined);

    if (recordsToPrint.length === 0) {
      alert("ไม่พบข้อมูลที่เลือก");
      return;
    }

    // ดึงข้อมูลเต็มของแต่ละ record จาก backend ก่อนเปิด modal
    const enrichedRecords = await fetchReportRecords(recordsToPrint);
    if (enrichedRecords.length === 0) {
      alert("ไม่สามารถโหลดข้อมูลรายงานได้");
      return;
    }

    // ส่ง array ของ records ไปให้ modal
    setSelectedReportRecord(enrichedRecords);
    setIsHealthReportModalOpen(true);
  };

  const handleViewData = async (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      const reportRecord = await fetchReportRecord(record);
      setSelectedReportRecord(reportRecord || record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleRemoveTemplate = async (recordId, templateIndex) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (!record) return;

    // 🔥 ตรวจสอบว่ามี database record ID หรือไม่
    if (!record.dbRecordId) {
      console.error("❌ Cannot remove template: database record ID not found");
      alert("ไม่สามารถลบ template ได้ กรุณาลองใหม่อีกครั้ง");
      return;
    }

    const templateName = record.templates[templateIndex];
    const matchedTemplate = record.matchedTemplates?.find(
      (t) => t.template_name === templateName,
    );

    if (!matchedTemplate || !matchedTemplate.template_id) {
      console.error("Cannot find template ID for:", templateName);
      return;
    }

    try {
      // เรียก API เพื่อซ่อน template (persist ลง database)
      // 🔥 FIX: ใช้ record.dbRecordId แทน recordId (เพราะ recordId คือ HN จาก MFU API)
      await healthRecordsAPI.hideTemplate(
        record.dbRecordId,
        matchedTemplate.template_id,
        templateName,
      );

      // อัพเดท local state หลังจาก API สำเร็จ
      const updatedRecords = healthRecords.map((r) => {
        if (r.id === recordId) {
          const updatedTemplates = r.templates.filter(
            (_, index) => index !== templateIndex,
          );
          const updatedMatchedTemplates = r.matchedTemplates?.filter(
            (_, index) => index !== templateIndex,
          );
          const updatedTags = [];
          updatedMatchedTemplates?.forEach((t) => {
            updatedTags.push(...(t.tags || []));
          });

          return {
            ...r,
            templates: updatedTemplates,
            matchedTemplates: updatedMatchedTemplates,
            tags: [...new Set(updatedTags)],
          };
        }
        return r;
      });

      setHealthRecords(updatedRecords);
      setAllHealthRecords(updatedRecords);
    } catch (error) {
      console.error("❌ Error hiding template:", error);
    }
  };

  const handleViewHiddenTemplates = async (recordId) => {
    try {
      setSelectedRecordId(recordId);
      const response = await healthRecordsAPI.getHiddenTemplates(recordId);

      if (response.status === "success" && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);
        setIsHiddenTemplatesModalOpen(true);
      } else {
        console.error("❌ ไม่สามารถดึงข้อมูล Templates ที่ซ่อนได้");
      }
    } catch (error) {
      console.error("❌ Error fetching hidden templates:", error);
    }
  };

  const handleRestoreTemplate = async (templateId) => {
    if (!selectedRecordId) return;

    try {
      await healthRecordsAPI.unhideTemplate(selectedRecordId, templateId);

      // รีโหลดข้อมูล health records (คงตำแหน่ง/ค้นหาเดิมไว้)
      const currentFilters = {};
      if (startDate) currentFilters.dateStart = formatDateToYYYYMMDD(startDate);
      if (endDate) currentFilters.dateStop = formatDateStopToYYYYMMDD(endDate);
      await fetchHealthRecords(currentFilters, {
        pageNow: currentPage,
        preservePage: true,
      });

      // รีโหลดรายการ hidden templates
      const response =
        await healthRecordsAPI.getHiddenTemplates(selectedRecordId);
      if (response.status === "success" && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);

        // ถ้าไม่มี hidden templates แล้ว ปิด modal
        if (response.data.length === 0) {
          setIsHiddenTemplatesModalOpen(false);
        }
      }
    } catch (error) {
      console.error("❌ Error restoring template:", error);
    }
  };

  const handleSendResult = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      // ✅ เช็คจาก approvalStatus แทน isApproved เพื่อให้ถูกต้องกับสถานะ revised
      if (record.approvalStatus === "approved") {
        // ถ้าเป็นการส่งใหม่ (สถานะ approved) แสดง SendHealthResultModal
        setIsSendHealthResultModalOpen(true);
      } else {
        // ถ้าเป็นการส่งครั้งแรก (สถานะ pending หรือ revised) แสดง HealthResultModal
        setIsHealthResultModalOpen(true);
      }
    }
  };

  const handleTemplateClick = async (
    templateName,
    matchedTemplateData = null,
  ) => {
    try {
      let template = null;

      // ถ้ามี matchedTemplateData ใช้เลย แต่ต้อง fetch รายละเอียดเพิ่ม
      if (matchedTemplateData && matchedTemplateData.template_id) {
        const response = await templateAPI.getById(
          matchedTemplateData.template_id,
        );
        if (response.status === "success" && response.data) {
          template = response.data;
        }
      } else {
        // ถ้าไม่มี ให้ค้นหาจาก template name
        const response = await templateAPI.getAll();
        if (response.status === "success" && response.data) {
          template = response.data.find(
            (t) => t.template_name === templateName,
          );
        }
      }

      if (template) {
        // ฟังก์ชันตรวจสอบว่าพารามิเตอร์เป็นชนิด radio หรือไม่
        const isRadioParameter = (parameterName) => {
          const radioParams = [
            "Protein",
            "Urine Glucose",
            "Ketones",
            "Blood",
            "Leukocytes",
            "Nitrite",
            "Bilirubin",
            "Urobilinogen",
            "Color",
            "Clarity",
            "Color (Stool)",
            "Appearance (Stool)",
            "WBC (Stool)",
            "RBC (Stool)",
            "Parasite",
            "RBC Index",
            "HBsAb",
            "HBsAg",
            "HCV Ab",
            "Hemoglobin detection stool",
          ];
          return radioParams.includes(parameterName);
        };

        // ✅ แปลงข้อมูลให้ตรงกับ format ใหม่ของ TemplateDetailModal (เหมือน editTamplateModal.js)
        const formattedTemplate = {
          templateName: template.template_name,
          createdBy: template.created_by,
          tags: template.tags || [],
          gender:
            template.gender === "male"
              ? "ชาย"
              : template.gender === "female"
                ? "หญิง"
                : "ทั้งสองเพศ",
          ageRange: [template.age_min, template.age_max], // ✅ ใช้ array แทน string
          selectedParameters:
            template.parameters?.map((p) => ({
              name: p.parameter_name,
              unit: p.unit || "",
              normalValue: p.normal_range || "-",
              normalRange: p.normal_range_array || [0, 100],
              allNormalRanges: p.all_normal_ranges || [], // ✅ ส่ง allNormalRanges ด้วย!
            })) || [],
          parameterRanges:
            template.parameters?.reduce((acc, p) => {
              if (!isRadioParameter(p.parameter_name)) {
                acc[p.parameter_name] = [p.custom_min, p.custom_max];
              }
              return acc;
            }, {}) || {},
          parameterRadioValues:
            template.parameters?.reduce((acc, p) => {
              if (isRadioParameter(p.parameter_name)) {
                acc[p.parameter_name] = p.custom_value || "-";
              }
              return acc;
            }, {}) || {},
        };

        setSelectedTemplate(formattedTemplate);
        setIsModalOpen(true);
      } else {
        console.warn("Template not found:", templateName);
      }
    } catch (error) {
      console.error("Error loading template:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRecord(null);
  };

  const handleEdit = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setSelectedRecordId(record.dbRecordId); // ✓ ใช้ database record_id
      setIsEditModalOpen(true);
    }
  };

  const handleCloseHealthResultModal = () => {
    setIsHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApprove = (recordId) => {
    // Optimistic update: อัปเดตสถานะทันทีใน UI โดยไม่รอ reload
    const updateStatus = (records) =>
      records.map((r) =>
        r.id === recordId ? { ...r, approvalStatus: "approved" } : r,
      );
    setHealthRecords((prev) => updateStatus(prev));
    setAllHealthRecords((prev) => updateStatus(prev));

    // Reload ใน background เพื่อยืนยันข้อมูลจาก server
    const filters = {
      dateStart: formatDateToYYYYMMDD(startDate),
      dateStop: formatDateStopToYYYYMMDD(endDate),
    };
    fetchHealthRecords(filters);
  };

  const handleViewHistory = (recordId) => {
    const record = healthRecords.find((r) => r.id === recordId);
    if (record) {
      setHistorySelectedRecord(record);
      setIsHistoryModalOpen(true);
    }
  };

  const handleCloseSendHealthResultModal = () => {
    setIsSendHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmSendAgain = async () => {
    if (selectedRecord) {
      try {
        const recordIdToUse = selectedRecord.dbRecordId || selectedRecord.id;

        const response = await healthRecommendationAPI.getDraft(recordIdToUse);

        if (response.status === "success" && response.data) {
          const saveResponse = await healthRecommendationAPI.saveDraft(
            recordIdToUse,
            {
              doctor_advice: response.data.doctor_advice || "",
              nutrition_advice: response.data.nutrition_advice || "",
              exercise_advice: response.data.exercise_advice || "",
              sleep_advice: response.data.sleep_advice || "",
            },
            "admin",
          );

          if (saveResponse.status === "success") {
            const filters = {
              dateStart: formatDateToYYYYMMDD(startDate),
              dateStop: formatDateStopToYYYYMMDD(endDate),
            };
            // ✅ ไม่ส่ง keywordName/keywordCampaign → frontend filter จัดการเอง
            await fetchHealthRecords(filters);
          }
        }
      } catch (error) {
        console.error("❌ Error changing status to revised:", error);
      }
    }
    setIsSendHealthResultModalOpen(false);
    // ✅ ไม่ต้องเปิด HealthResultModal อัตโนมัติ ให้ user กดปุ่ม "กดเพื่อส่งผลสุขภาพ" เองหลังจาก status เปลี่ยนเป็น revised
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedReportRecord(null);
  };

  // State สำหรับเก็บข้อมูลจาก API
  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]); // เก็บข้อมูลทั้งหมดไว้สำหรับ filter
  const [loading, setLoading] = useState(true);
  const [masterData, setMasterData] = useState(null);

  // โหลด Master Data จาก API (เหมือน Super Admin)
  // โหลด Master Data และ Companies จาก API
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const result = await referenceDataAPI.getAllParameters();
        if (result.status === "success" && result.data) {
          setMasterData(result.data);
        }
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };

    const loadCompanies = async () => {
      try {
        const firstRes = await campaignAPI.getList(1);
        if (firstRes.result && Array.isArray(firstRes.list)) {
          let allCampaigns = [...firstRes.list];
          const pageAmount = firstRes.pageAmount || 1;
          for (let page = 2; page <= pageAmount; page++) {
            const pageRes = await campaignAPI.getList(page);
            if (pageRes.result && Array.isArray(pageRes.list)) {
              allCampaigns = allCampaigns.concat(pageRes.list);
            }
          }
          setCompanies(
            allCampaigns.filter((item) => item.name && item.name !== "-"),
          );
        }
      } catch (error) {
        console.error("Error loading companies:", error);
      }
    };

    loadMasterData();
    loadCompanies();
  }, []);

  // ฟังก์ชันแปลงชื่อ parameter ให้ตรงกับ key ใน medium.json
  const normalizeParameterName = (paramName) => {
    const mapping = {
      // Blood Chemistry & Liver Function
      "SGOT (AST)": "SGOT (AST)",
      "SGPT (ALT)": "SGPT (ALT)",
      "ALP (Alkaline Phosphatase)": "ALP",
      ALP: "ALP",
      "Uric Acid": "Uric Acid",
      BUN: "BUN, Urea",
      GGT: "GGT",
      Cholesterol: "คอเลสเตอรอลรวม",
      Triglyceride: "ไตรกลีเซอไรด์",
      CHO: "คอเลสเตอรอลรวม",
      TRI: "ไตรกลีเซอไรด์",
      FBS: "Fasting blood glucose",

      // CBC - Blood Cell Count
      Basophil: "Basophil",
      Platelet: "Platelets",
      "Platelets count": "Platelets count",
      Hypochromic: "Hypochromic",
      Microcyte: "Microcyte",
      Others: "Others",

      // Urine Tests
      "Urine Color": "Color",
      "Urine Appearance": "Urine Appearance",
      Appearance: "Urine Appearance", // กรณีที่ส่งมาแค่ "Appearance"
      Clarity: "Clarity",
      "Urine pH": "pH",
      "Specific Gravity": "Specific Gravity",
      "Urine Protein": "Protein",
      Protein: "Protein",
      "Urine Glucose": "Sugar",
      Glucose: "Fasting blood glucose", // แก้ไข: Glucose ในเลือด ไม่ใช่ปัสสาวะ
      "Glucose (FBS)": "Fasting blood glucose", // แก้ไข: FBS = Fasting blood glucose
      "Urine Ketone": "Urine Ketone",
      Ketone: "Urine Ketone", // กรณีที่ส่งมาแค่ "Ketone"
      "Urine Blood": "Blood",
      "Urine Leukocytes": "Leukocytes",
      "Urine Nitrite": "Nitrite",
      "Urine Bilirubin": "Bilirubin",
      Urobilinogen: "Urobilinogen",
      "Urine RBC": "RBC (Urine)",
      "Urine WBC": "WBC (Urine)",
      "Urine Bacteria": "Bacteria",
      Bacteria: "Bacteria",
      "Epithelial Cells": "Epithelial cell", // แก้ไข: API ส่งมา "Epithelial Cells" แต่ใน medium.json เป็น "Epithelial cell"
      Epithelial: "Epithelial cell", // แก้ไข: API ส่งมา "Epithelial" แต่ใน medium.json เป็น "Epithelial cell"

      // Urine Crystals - ✅ เพิ่มคำว่า "crystal" ท้ายชื่อ
      "Calcium Oxalate": "Calcium oxalate crystal",
      "Calcium oxalate": "Calcium oxalate crystal",
      "Uric Acid Crystal": "Uric acid crystal",
      "Uric acid": "Uric acid crystal",
      "Triple Phosphate": "Triple phosphate crystal",
      "Triple phosphate": "Triple phosphate crystal",
      "Calcium Carbonate": "Calcium Carbonate crystal",
      "Calcium carbonate": "Calcium Carbonate crystal",
      "Calcium Phosphate": "Calcium phosphate crystal",
      "Calcium phosphate": "Calcium phosphate crystal",
      Cystine: "Cystine crystal",
      "Cystine Crystal": "Cystine crystal",

      // Urine Casts - ✅ อยู่ในหมวด "ผลการตรวจปัสสาวะ"
      "Hyaline Cast": "Hyaline cast",
      "Granular Cast": "Granular cast",
      "Cellular Cast": "Cellular cast",
      "Waxy Cast": "Waxy cast",
      "RBC cast": "RBC cast",

      // Stool Tests
      "Stool Color": "Color (Stool)",
      "Stool WBC": "WBC (Stool)",
      "Stool RBC": "RBC (Stool)",
      "Parasite, Ova": "Parasite",
      Parasite: "Parasite",
      Consistency: "Consistency",

      // Electrolytes
      Sodium: "Sodium",
      Potassium: "Potassium",
      Chloride: "Chloride",
      Bicarbonate: "Bicarbonate",
      "Total Calcium": "Total Calcium",
      Calcium: "Total Calcium",
      "Inorganic phosphate": "Inorganic phosphate",
      Magnesium: "Magnesium",

      // Tumor Markers
      AFP: "AFP",
      PSA: "PSA",
      CEA: "CEA",
      "CA19-9": "CA19-9",
      CA125: "CA125",
      CA153: "CA153",

      // Hepatitis Virus
      HBsAb: "HBsAb",
      "Anti-HBs Concentration": "Anti-HBs Concentration",
      "HCV Ab": "HCV Ab",
      "anti-HCV": "HCV Ab",
      "HCV Ab COI": "COI",

      // Thyroid Hormones
      "Free T3": "Free T3",
      "Free T4": "Free T4",
      TSH: "TSH",

      // Other Tests
      Cholinesterase: "Cholinesterase",
      "Direct bilirubin": "Direct bilirubin",

      // Physical Examination
      BMI: "BMI",
      BPS: "BPS",
      BPD: "BPD",
      Pulse: "Pulse",
    };
    return mapping[paramName] || paramName;
  };

  // ฟังก์ชันหาค่าปกติจาก Master Data (เหมือน Super Admin)
  const getNormalRangeFromMaster = (paramName, sex, age) => {
    if (!masterData) {
      return "-";
    }

    const searchName = normalizeParameterName(paramName);

    // ✅ ลองหาทั้งชื่อปกติและชื่อที่มี _hide_ prefix
    const searchNames = [searchName, `_hide_${searchName}`];

    for (const category in masterData) {
      const categoryData = masterData[category];

      // ลองหาทั้ง 2 ชื่อ (แบบ case-insensitive)
      for (const name of searchNames) {
        // 🔍 ค้นหาแบบ case-insensitive
        const matchingKey = Object.keys(categoryData).find(
          (key) => key.toLowerCase() === name.toLowerCase(),
        );

        if (matchingKey && categoryData[matchingKey]) {
          const ranges = categoryData[matchingKey];
          for (const range of ranges) {
            const sexMatch = range.sex === "F,M" || range.sex === sex;
            const ageMatch = age >= range.age_min && age <= range.age_max;

            if (sexMatch && ageMatch) {
              return range.normal_range;
            }
          }
        }
      }
    }
    return "-";
  };

  // ฟังก์ชันหาหน่วยจาก Master Data
  const getUnitFromMaster = (paramName) => {
    if (!masterData) {
      return "";
    }

    // ✅ ใช้ normalizeParameterName เพื่อแปลงชื่อให้ตรงกับ medium.json
    const searchName = normalizeParameterName(paramName);

    // ✅ ลองหาทั้งชื่อปกติและชื่อที่มี _hide_ prefix
    const searchNames = [searchName, `_hide_${searchName}`];

    for (const category in masterData) {
      const categoryData = masterData[category];

      // ลองหาทั้ง 2 ชื่อ (แบบ case-insensitive)
      for (const name of searchNames) {
        // 🔍 ค้นหาแบบ case-insensitive
        const matchingKey = Object.keys(categoryData).find(
          (key) => key.toLowerCase() === name.toLowerCase(),
        );

        if (matchingKey && categoryData[matchingKey]) {
          const ranges = categoryData[matchingKey];
          if (ranges && ranges.length > 0 && ranges[0].unit) {
            return ranges[0].unit;
          }
        }
      }
    }
    return "";
  };

  // ฟังก์ชันจัดหมวดหมู่พารามิเตอร์ (ใช้หมวดหมู่จาก masterData/medium.json)
  const categorizeParameters = (parameters) => {
    if (!masterData) {
      return { ทั้งหมด: parameters };
    }

    const categorized = {};
    const usedParams = new Set();

    // ใช้หมวดหมู่จาก masterData (medium.json) โดยตรง
    for (const categoryName in masterData) {
      const categoryParams = masterData[categoryName];
      const paramNamesInCategory = Object.keys(categoryParams);

      // หา parameters ที่อยู่ในหมวดนี้
      categorized[categoryName] = parameters.filter((param) => {
        // ค้นหาแบบ case-insensitive
        const isMatch = paramNamesInCategory.some((masterParamName) => {
          // ลบ _hide_ prefix ออกก่อนเปรียบเทียบ
          const cleanMasterName = masterParamName.replace(/^_hide_/, "");

          // ✅ ใช้ normalizeParameterName เพื่อแปลงชื่อก่อนเปรียบเทียบ
          const normalizedParamName = normalizeParameterName(param.name);
          const paramLower = normalizedParamName.toLowerCase();
          const masterLower = cleanMasterName.toLowerCase();
          const isMucusThread =
            paramLower.replace(/\s*\(urine\)$/, "").trim() === "mucus thread";

          // บังคับให้ Mucus thread อยู่ในหมวดผลการตรวจปัสสาวะเท่านั้น
          if (isMucusThread) {
            return categoryName === "ผลการตรวจปัสสาวะ";
          }

          // ✅ จับคู่แบบต่างๆ
          if (masterLower === paramLower) return true;

          // ✅ จับคู่แบบมี suffix ในวงเล็บ เช่น "ALP (Alkaline Phosphatase)" -> "ALP"
          // ⚠️ ข้ามกรณีที่วงเล็บเป็น qualifier ของตัวอย่าง เช่น "(Urine)", "(Stool)" เพราะเป็นคนละหมวด
          if (paramLower.includes(" (")) {
            const paramBase = paramLower.split(" (")[0].trim();
            const parenContent = paramLower.split(" (")[1] || "";
            const isSpecimenQualifier =
              parenContent.includes("urine") || parenContent.includes("stool");
            if (!isSpecimenQualifier && paramBase === masterLower) return true;
          }

          // ✅ จับคู่แบบพิเศษ
          if (paramLower === "basophil" && masterLower === "basophile")
            return true;
          if (
            paramLower === "epithelial cells" &&
            masterLower === "epithelial cell"
          )
            return true;
          if (paramLower === "epithelial" && masterLower === "epithelial cell")
            return true;

          return false;
        });

        if (isMatch) usedParams.add(param.name);
        return isMatch;
      });
    }

    // พารามิเตอร์ที่ไม่อยู่ในหมวดใดๆ ใส่ใน "อื่นๆ"
    const unusedParams = parameters.filter(
      (param) => !usedParams.has(param.name),
    );
    if (unusedParams.length > 0) {
      categorized["อื่นๆ"] = unusedParams;
    }

    // ลบหมวดที่ไม่มีพารามิเตอร์
    return Object.fromEntries(
      Object.entries(categorized).filter(([_, params]) => params.length > 0),
    );
  };

  // ฟังก์ชันปรับค่าผลตรวจให้ตรงกับหลักของค่าปกติ
  const normalizeResultValue = (resultValue, normalRange, paramName = "") => {
    if (paramName === "Anti-HBs Concentration") {
      return resultValue;
    }

    if (
      !resultValue ||
      !normalRange ||
      normalRange === "-" ||
      normalRange === "xxx"
    ) {
      return resultValue;
    }

    // ลบ comma และแปลงเป็นตัวเลข
    const cleanValue =
      typeof resultValue === "string"
        ? resultValue.replace(/,/g, "")
        : resultValue;
    const result = parseFloat(cleanValue);

    if (isNaN(result)) {
      return resultValue;
    }

    // หาค่า max ของช่วงปกติเพื่อใช้เป็นตัวอ้างอิง
    let referenceValue = null;

    if (normalRange.includes("-")) {
      const [min, max] = normalRange
        .split("-")
        .map((v) => parseFloat(v.trim()));
      referenceValue = Math.max(min, max);
    } else if (normalRange.startsWith("<")) {
      referenceValue = parseFloat(normalRange.replace("<", "").trim());
    } else if (normalRange.startsWith(">")) {
      referenceValue = parseFloat(normalRange.replace(">", "").trim());
    }

    if (!referenceValue || isNaN(referenceValue)) {
      return resultValue;
    }

    // ถ้าค่าผลตรวจมากกว่าค่าอ้างอิงมากกว่า 100 เท่า ให้หาร 1000
    if (result > referenceValue * 100) {
      const normalized = result / 1000;
      // ปัดทศนิยมให้เหลือสูงสุด 2 ตำแหน่ง
      return Number.isInteger(normalized)
        ? normalized.toString()
        : normalized.toFixed(2);
    }
    // ถ้าค่าผลตรวจมากกว่าค่าอ้างอิงมากกว่า 10 เท่า ให้หาร 100
    else if (result > referenceValue * 10) {
      const normalized = result / 100;
      return Number.isInteger(normalized)
        ? normalized.toString()
        : normalized.toFixed(2);
    }

    return resultValue;
  };

  const ORDERED_DROPDOWN_VALUES = ["negative", "trace", "1+", "2+", "3+", "4+"];

  const normalizeOrderedDropdownValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    const text = String(value).trim().toLowerCase().replace(/\s+/g, "");
    const match = text.match(/negative|trace|1\+|2\+|3\+|4\+/);
    return match ? match[0] : text;
  };

  const compareOrderedDropdownRange = (normalRange, resultValue) => {
    const resultIdx = ORDERED_DROPDOWN_VALUES.indexOf(
      normalizeOrderedDropdownValue(resultValue),
    );
    if (resultIdx === -1) {
      return null;
    }

    const normalizedRange = String(normalRange || "")
      .split("-")
      .map((value) => normalizeOrderedDropdownValue(value));

    if (normalizedRange.length === 2) {
      const minIdx = ORDERED_DROPDOWN_VALUES.indexOf(normalizedRange[0]);
      const maxIdx = ORDERED_DROPDOWN_VALUES.indexOf(normalizedRange[1]);

      if (minIdx !== -1 && maxIdx !== -1) {
        return resultIdx >= minIdx && resultIdx <= maxIdx;
      }
    }

    const exactIdx = ORDERED_DROPDOWN_VALUES.indexOf(
      normalizeOrderedDropdownValue(normalRange),
    );
    if (exactIdx !== -1) {
      return resultIdx === exactIdx;
    }

    return null;
  };

  // ฟังก์ชันเปรียบเทียบค่าปกติกับค่าที่ตรวจวัดได้
  const compareValues = (normalRange, resultValue, paramName = "") => {
    const normalizeComparisonParamName = (name) => {
      const aliases = {
        "Urine RBC": "RBC (Urine)",
        "Urine WBC": "WBC (Urine)",
        "Epithelial Cells": "Epithelial cell",
      };

      return aliases[name] || name;
    };

    const normalizedParamName = normalizeComparisonParamName(paramName);

    const parseComparableRange = (value) => {
      if (value === null || value === undefined) {
        return null;
      }

      const text = String(value).trim().replace(/,/g, "").replace(/\s+/g, "");
      const cleanedText = text.replace(
        /(cells\/hpf|cells\/lpf|\/hpf|\/lpf|cells\/ul|cells\/ฮผl|cells\/เธขเธ•l)$/i,
        "",
      );

      const rangeMatch = cleanedText.match(
        /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)/,
      );
      if (rangeMatch) {
        return {
          min: parseFloat(rangeMatch[1]),
          max: parseFloat(rangeMatch[2]),
          minInclusive: true,
          maxInclusive: true,
          raw: cleanedText,
        };
      }

      const lessThanMatch = cleanedText.match(/^<(-?\d+(?:\.\d+)?)/);
      if (lessThanMatch) {
        return {
          min: Number.NEGATIVE_INFINITY,
          max: parseFloat(lessThanMatch[1]),
          minInclusive: false,
          maxInclusive: false,
          raw: cleanedText,
        };
      }

      const greaterThanMatch = cleanedText.match(/^>(-?\d+(?:\.\d+)?)/);
      if (greaterThanMatch) {
        return {
          min: parseFloat(greaterThanMatch[1]),
          max: Number.POSITIVE_INFINITY,
          minInclusive: false,
          maxInclusive: false,
          raw: cleanedText,
        };
      }

      const singleValueMatch = cleanedText.match(/^(-?\d+(?:\.\d+)?)/);
      if (singleValueMatch) {
        const num = parseFloat(singleValueMatch[1]);
        return {
          min: num,
          max: num,
          minInclusive: true,
          maxInclusive: true,
          raw: cleanedText,
        };
      }

      return null;
    };

    const isRangeWithinRange = (resultRange, normalRange) => {
      // ผลตรวจแบบเปิดปลาย เช่น "<1.3" หรือ ">200" (พบบ่อยกับค่าบ่งชี้มะเร็งที่ต่ำ/สูงกว่าขีดจำกัดการตรวจวัด)
      // เช็คแบบ "ต้องอยู่ในช่วงทั้งหมด" ไม่ได้เพราะขอบเขตเป็นอนันต์ ให้เช็คว่าไม่มีทางซ้อนทับกันเลยแทน (ไม่ซ้อนทับ = ผิดปกติ)
      const resultIsOpenEnded =
        resultRange.min === Number.NEGATIVE_INFINITY ||
        resultRange.max === Number.POSITIVE_INFINITY;

      if (resultIsOpenEnded) {
        const noOverlap =
          resultRange.max < normalRange.min ||
          resultRange.min > normalRange.max;
        return !noOverlap;
      }

      const minWithin =
        resultRange.min > normalRange.min ||
        (resultRange.min === normalRange.min &&
          (normalRange.minInclusive || !resultRange.minInclusive));
      const maxWithin =
        resultRange.max < normalRange.max ||
        (resultRange.max === normalRange.max &&
          (normalRange.maxInclusive || !resultRange.maxInclusive));

      return minWithin && maxWithin;
    };

    if (
      !normalRange ||
      !resultValue ||
      normalRange === "xxx" ||
      resultValue === "xxx" ||
      normalRange === "-" ||
      resultValue === "-"
    ) {
      return { status: "normal", label: "-" };
    }

    if (normalizedParamName === "Stool Color") {
      return { status: "normal", label: "-" };
    }

    if (normalizedParamName === "RBC morphology") {
      const normalizedNormal = String(normalRange).trim().toLowerCase();
      const normalizedResult = String(resultValue).trim().toLowerCase();

      if (normalizedNormal && normalizedResult === normalizedNormal) {
        return { status: "normal", label: "Normal" };
      }

      return { status: "high", label: "Abnormal" };
    }

    // ✅ ถ้า normalRange เป็น text (มีตัวอักษร แต่ไม่ใช่ format "M x-y, F x-y")
    // ต้องเช็คก่อน parseFloat เพราะ parseFloat("3+(300mg/dL)") = 3 ทำให้หลุดรอดไปเป็น Normal
    const normalRangeText = String(normalRange).trim();
    const resultValueText = String(resultValue).trim();
    const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRangeText);
    const isCountDropdownParam = [
      "RBC (Urine)",
      "WBC (Urine)",
      "Epithelial cell",
      "Red Blood Cells",
      "White Blood Cells",
      "WBC (Stool)",
      "RBC (Stool)",
    ].includes(normalizedParamName);

    if (isCountDropdownParam) {
      const normalizedNormal = parseComparableRange(normalRangeText);
      const normalizedResult = parseComparableRange(resultValueText);
      if (normalizedNormal && normalizedResult) {
        if (isRangeWithinRange(normalizedResult, normalizedNormal)) {
          return { status: "normal", label: "Normal" };
        }
        return { status: "high", label: "Abnormal" };
      }
    }

    const orderedDropdownMatch = compareOrderedDropdownRange(
      normalRangeText,
      resultValueText,
    );
    if (orderedDropdownMatch !== null) {
      return orderedDropdownMatch
        ? { status: "normal", label: "Normal" }
        : { status: "high", label: "Abnormal" };
    }
    if (/[a-zA-Z]/.test(normalRangeText) && !normalRangeIsGenderFormat) {
      const normalizedResult = resultValue
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");
      const normalValues = normalRange
        .split(",")
        .map((v) => v.trim().toLowerCase().replace(/\s+/g, ""));
      if (normalValues.includes(normalizedResult)) {
        return { status: "normal", label: "Normal" };
      } else {
        return { status: "high", label: "High" };
      }
    }

    const normalizedNormalRange = parseComparableRange(normalRangeText);
    const normalizedResultRange = parseComparableRange(resultValueText);

    // ถ้าทั้ง normalRange และ resultValue เป็นช่วงตัวเลข ต้องเทียบเป็นช่วงจริง
    // ไม่ใช่แค่เอาเลขตัวหน้าไป parseFloat เพราะจะทำให้ 2-3 ไปกลายเป็น 2
    if (normalizedNormalRange && normalizedResultRange) {
      if (isRangeWithinRange(normalizedResultRange, normalizedNormalRange)) {
        return { status: "normal", label: "Normal" };
      }

      return { status: "high", label: "Abnormal" };
    }

    // แปลงค่าที่ตรวจวัดได้เป็นตัวเลข
    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: "normal", label: "-" };
    }

    // แยกช่วงค่าปกติ (เช่น "100-200" หรือ "<100" หรือ ">100")
    if (normalRangeText.includes("-")) {
      const [min, max] = normalRange
        .split("-")
        .map((v) => parseFloat(v.trim()));
      if (result < min) {
        return { status: "low", label: "Low" };
      } else if (result > max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRangeText.startsWith("<")) {
      const max = parseFloat(normalRangeText.replace("<", "").trim());
      if (result >= max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRangeText.startsWith(">")) {
      const min = parseFloat(normalRangeText.replace(">", "").trim());
      if (result <= min) {
        return { status: "low", label: "Low" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    }

    return { status: "normal", label: "Normal" };
  };

  // ดึงข้อมูลทั้งหมดเมื่อ component โหลด (ไม่กรองวันที่)
  // ให้ User ค้นหาตามวันที่เอง
  useEffect(() => {
    if (masterData) {
      const filters = {
        dateStart: formatDateToYYYYMMDD(defaultStartDate),
        dateStop: formatDateStopToYYYYMMDD(defaultEndDate),
      };
      hasSearchedRef.current = true;
      fetchHealthRecords(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterData]);

  // Scroll to top เมื่อเปลี่ยนหน้า
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Filter ข้อมูลตาม status เมื่อ allHealthRecords หรือ status เปลี่ยน
  // (keyword/company filter ทำฝั่ง backend แล้ว, status filter ทำ client-side เฉพาะหน้าปัจจุบัน)
  useEffect(() => {
    if (allHealthRecords.length === 0) {
      setHealthRecords([]);
      return;
    }

    let filtered = [...allHealthRecords];

    // Filter ตามสถานะ client-side เฉพาะกรณี pending
    // (approved/revised ถูก filter โดย backend endpoint แล้ว ไม่ต้อง filter ซ้ำ)
    if (status) {
      if (
        status !== "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว" &&
        status !== "มีการแก้ไขรอการอนุมัติอีกครั้ง"
      ) {
        // pending: filter client-side เหมือนเดิม
        filtered = filtered.filter(
          (record) => record.approvalStatus === "pending",
        );
      }
      // approved/revised: backend แก้ไขแล้ว ไม่ต้อง filter เพิ่ม
    }

    setHealthRecords(filtered);
  }, [allHealthRecords, status]);

  // ฟังก์ชัน Match templates กับ health records
  const matchTemplatesWithRecords = async (records, allTemplates) => {
    try {
      if (!allTemplates || allTemplates.length === 0) return;

      // Match ทุก record พร้อมกัน (parallel) แทนที่จะวนทีละตัว (sequential)
      await Promise.all(
        records.map(async (record) => {
          // ดึง manual templates และ hidden templates พร้อมกัน (parallel) ต่อ record
          let manualTemplates = null;
          const hiddenIdSet = new Set();
          const manualIdSet = new Set();

          if (record.dbRecordId) {
            const [manualResponse, hiddenResponse] = await Promise.all([
              healthRecordsAPI
                .getManualTemplates(record.dbRecordId)
                .catch(() => null),
              healthRecordsAPI
                .getHiddenTemplates(record.dbRecordId)
                .catch(() => null),
            ]);

            if (
              manualResponse?.status === "success" &&
              manualResponse.data?.length > 0
            ) {
              manualTemplates = manualResponse.data;
              manualTemplates.forEach((mt) =>
                manualIdSet.add(Number(mt.template_id)),
              );
            }

            if (
              hiddenResponse?.status === "success" &&
              Array.isArray(hiddenResponse.data)
            ) {
              hiddenResponse.data.forEach((h) =>
                hiddenIdSet.add(Number(h.template_id)),
              );
            }
          }

          const matchedTemplates = [];
          const allTags = new Set();

          // แปลง gender เพื่อ match
          const recordGender = record.gender === "ชาย" ? "male" : "female";
          const recordAge = parseInt(record.age) || 0;
          const ageKnown = record.age !== "-" && recordAge > 0;

          // สร้าง set ของ parameter names ที่มีใน record
          const recordParameterNames = new Set(
            record.parameters.map((p) => p.name),
          );

          for (const template of allTemplates) {
            // ตรวจสอบว่า template active หรือไม่
            if (Number(template.is_active) !== 1) continue;

            const tid = Number(template.id);

            // ถ้า admin เคยซ่อน template นี้ไปแล้ว → ข้ามไปเลย ไม่ auto-match
            if (hiddenIdSet.has(tid)) continue;

            // ถ้า template นี้อยู่ใน manual list แล้ว → ไม่ต้อง auto-match ซ้ำ (จะเพิ่มทีหลัง)
            if (manualIdSet.has(tid)) continue;

            // ตรวจสอบ gender
            const templateGender = template.gender;
            if (templateGender !== "both" && templateGender !== recordGender)
              continue;

            // ตรวจสอบ age range — ถ้าไม่รู้อายุ (โหลดแบบ bulk ไม่มี profile.age) ให้ข้าม age check
            if (
              ageKnown &&
              (recordAge < template.age_min || recordAge > template.age_max)
            )
              continue;

            // ตรวจสอบว่ามี parameters ตรงกันหรือไม่
            const templateParams = template.parameters || [];
            let matchCount = 0;
            let totalParams = templateParams.length;

            // แมปชื่อ parameter จาก medium.json → ชื่อ parameter ใน health record
            const mediumToRecordParam = {
              คอเลสเตอรอลรวม: "Cholesterol",
              ไตรกลีเซอไรด์: "Triglyceride",
              "BUN, Urea": "BUN",
              ALP: "ALP (Alkaline Phosphatase)",
              "น้ำตาล (Sugar)": "Sugar",
              เซลล์เยื่อบุผิว: "Epithelial cell",
              "Color (Stool)": "Stool Color",
              "Consistency (Stool)": "Consistency",
              "WBC (Stool)": "Stool WBC",
              "RBC (Stool)": "Stool RBC",
              "RBC (Urine)": "Urine RBC",
              "WBC (Urine)": "Urine WBC",
              "Epithelial cell": "Epithelial Cells",
              Protein: "Urine Protein",
            };

            // ตัด prefix "_hide_" ออกก่อนเทียบชื่อ เพราะพารามิเตอร์ที่ซ่อนใน master data
            // (เช่น ผลึกต่างๆ, epithelial cell บางชนิด) เก็บชื่อในเทมเพลตพร้อม prefix
            // แต่ผลตรวจจริงใน record ไม่มี prefix
            const stripHidePrefix = (name) =>
              typeof name === "string" && name.startsWith("_hide_")
                ? name.substring(6)
                : name;

            // ลำดับสำหรับ count dropdown parameters
            const COUNT_DROPDOWN_OPTIONS = [
              "0-1",
              "1-2",
              "2-3",
              "3-5",
              "5-10",
              "10-20",
              "20-30",
              "30-50",
              "50-100",
              "100-200",
              ">100",
              ">200",
            ];
            const COUNT_DROPDOWN_PARAMS = [
              "RBC (Urine)",
              "WBC (Urine)",
              "Epithelial cell",
              "Red Blood Cells",
              "White Blood Cells",
            ];

            const COUNT_DROPDOWN_PARAMS_WITH_STOOL = [
              ...COUNT_DROPDOWN_PARAMS,
              "WBC (Stool)",
              "RBC (Stool)",
              "เซลล์เยื่อบุผิว",
              "Non-squamous epithelial cell",
              "Renal tubular epithelial cell",
              "Calcium oxalate crystal",
              "Uric acid crystal",
              "Triple phosphate crystal",
              "Calcium Carbonate crystal",
              "Calcium phosphate crystal",
              "Cystine crystal",
            ];

            for (const templateParam of templateParams) {
              const cleanParamName = stripHidePrefix(
                templateParam.parameter_name,
              );
              const mappedName =
                mediumToRecordParam[cleanParamName] ||
                mediumToRecordParam[templateParam.parameter_name] ||
                cleanParamName;
              // ลอง mapped name ก่อน ถ้าไม่เจอ ลอง clean name แล้วค่อย original template param name
              const recordParamName = recordParameterNames.has(mappedName)
                ? mappedName
                : recordParameterNames.has(cleanParamName)
                  ? cleanParamName
                  : recordParameterNames.has(templateParam.parameter_name)
                    ? templateParam.parameter_name
                    : mappedName;
              if (recordParameterNames.has(recordParamName)) {
                const recordParam = record.parameters.find(
                  (p) => p.name === recordParamName,
                );
                if (recordParam && recordParam.result) {
                  const isCountParam =
                    COUNT_DROPDOWN_PARAMS_WITH_STOOL.includes(cleanParamName);

                  if (isCountParam) {
                    // normalize: ลบ unit suffix เช่น "Cells/HPF", "/HPF", "/LPF" ออกก่อนเปรียบเทียบ
                    const stripUnit = (v) =>
                      (v || "")
                        .replace(/cells\/hpf/i, "")
                        .replace(/\/hpf/i, "")
                        .replace(/\/lpf/i, "")
                        .replace(/cells\/µl/i, "")
                        .trim();
                    // เปรียบเทียบโดยใช้ index ใน COUNT_DROPDOWN_OPTIONS
                    const valueIdx = COUNT_DROPDOWN_OPTIONS.indexOf(
                      stripUnit(recordParam.result),
                    );
                    const minIdx = COUNT_DROPDOWN_OPTIONS.indexOf(
                      stripUnit(templateParam.custom_min),
                    );
                    const maxIdx = COUNT_DROPDOWN_OPTIONS.indexOf(
                      stripUnit(templateParam.custom_max),
                    );

                    if (valueIdx !== -1 && minIdx !== -1 && maxIdx !== -1) {
                      if (valueIdx >= minIdx && valueIdx <= maxIdx) {
                        matchCount++;
                      }
                    }
                  } else {
                    // เปรียบเทียบแบบ numeric สำหรับพารามิเตอร์ทั่วไป
                    const value = parseFloat(recordParam.result);
                    const customMin = parseFloat(templateParam.custom_min);
                    const customMax = parseFloat(templateParam.custom_max);

                    if (
                      !isNaN(value) &&
                      !isNaN(customMin) &&
                      !isNaN(customMax)
                    ) {
                      if (value >= customMin && value <= customMax) {
                        matchCount++;
                      }
                    } else {
                      // Text fallback: สำหรับ param ที่เป็น text (เช่น HBsAb = "Positive")
                      // custom_value ใช้สำหรับ radio params, custom_min สำหรับ numeric/dropdown
                      const resultText = (recordParam.result || "")
                        .trim()
                        .toLowerCase();
                      const targetText = (
                        templateParam.custom_value ||
                        templateParam.custom_min ||
                        ""
                      )
                        .trim()
                        .toLowerCase();
                      if (targetText && resultText === targetText) {
                        matchCount++;
                      }
                    }
                  }
                }
              }
            }

            // ต้อง match ครบทุก parameters (100%) ถึงจะ match (AND logic)
            if (totalParams > 0 && matchCount === totalParams) {
              matchedTemplates.push({
                template_id: tid,
                template_name: template.template_name,
                match_percentage: Math.round((matchCount / totalParams) * 100),
                tags: template.tags || [],
              });

              if (template.tags && Array.isArray(template.tags)) {
                template.tags.forEach((tag) => allTags.add(tag));
              }
            }
          }

          // เพิ่ม manual templates (ที่ admin เลือกไว้โดยตรง) เข้าไปด้วย
          if (manualTemplates && manualTemplates.length > 0) {
            manualTemplates.forEach((mt) => {
              matchedTemplates.push(mt);
              if (mt.tags && Array.isArray(mt.tags)) {
                mt.tags.forEach((tag) => allTags.add(tag));
              }
            });
          }

          // Deduplicate templates by template_name (prevents same-name templates from showing twice)
          const seenNames = new Set();
          const dedupedTemplates = matchedTemplates.filter((mt) => {
            const name = mt.template_name;
            if (seenNames.has(name)) return false;
            seenNames.add(name);
            return true;
          });

          const templateLookup = new Map(
            allTemplates.map((template) => [Number(template.id), template]),
          );

          const templateRangeOverrides = new Map();
          const templateParamAliasMap = {
            "Color (Stool)": "Stool Color",
            "Consistency (Stool)": "Consistency",
            "Epithelial cell": "Epithelial Cells",
            "RBC (Stool)": "Stool RBC",
            "RBC (Urine)": "Urine RBC",
            "WBC (Stool)": "Stool WBC",
            "WBC (Urine)": "Urine WBC",
            "BUN, Urea": "BUN",
            ALP: "ALP (Alkaline Phosphatase)",
            Bilirubin: "Urine Bilirubin",
            Blood: "Urine Blood",
            Cholesterol: "Cholesterol",
            Glucose: "Sugar",
            Ketones: "Urine Ketone",
            Leukocytes: "Urine Leukocytes",
            Nitrite: "Urine Nitrite",
            Protein: "Urine Protein",
            Sugar: "Urine Glucose",
            Urobilinogen: "Urobilinogen",
          };
          dedupedTemplates.forEach((matchedTemplate) => {
            const template = templateLookup.get(
              Number(matchedTemplate.template_id),
            );
            if (!template?.parameters || !Array.isArray(template.parameters)) {
              return;
            }

            template.parameters.forEach((templateParam) => {
              const paramName = templateParam.parameter_name;
              const mappedName = templateParamAliasMap[paramName] || paramName;
              const overrideValue = templateParam.custom_value
                ? templateParam.custom_value
                : templateParam.custom_min !== null &&
                    templateParam.custom_min !== undefined &&
                    templateParam.custom_max !== null &&
                    templateParam.custom_max !== undefined
                  ? `${templateParam.custom_min}-${templateParam.custom_max}`
                  : null;

              if (!overrideValue) return;

              [paramName, mappedName].filter(Boolean).forEach((nameKey) => {
                if (!templateRangeOverrides.has(nameKey)) {
                  templateRangeOverrides.set(nameKey, overrideValue);
                }
              });
            });
          });

          if (
            false &&
            templateRangeOverrides.size > 0 &&
            Array.isArray(record.parameters)
          ) {
            record.parameters = record.parameters.map((param) => {
              const override = templateRangeOverrides.get(param.name);
              if (!override) {
                return param;
              }

              return {
                ...param,
                normal: override,
                templateNormal: override,
              };
            });
          }

          record.templateParameterOverrides = {};

          // อัพเดท record ด้วย matched templates และ tags
          record.matchedTemplates = dedupedTemplates;
          record.tags = Array.from(allTags);
          record.templates = dedupedTemplates.map((mt) => mt.template_name);
        }),
      ); // ✅ ปิด Promise.all — ทุก record ทำงาน parallel
    } catch (error) {
      console.error("❌ Error matching templates:", error);
    }
  };

  const fetchHealthRecords = async (
    filters = {},
    { pageNow = 1, preservePage = false } = {},
  ) => {
    const currentFetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      currentFiltersRef.current = filters;
      if (!preservePage) setCurrentPage(pageNow);

      const apiFilters = { ...filters };

      // แปลง statusFilter (Thai label) → statusEnum (approved/revised)
      const statusFilterLabel = filters.statusFilter || "";
      let statusEnum = "";
      if (statusFilterLabel === "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว")
        statusEnum = "approved";
      else if (statusFilterLabel === "มีการแก้ไขรอการอนุมัติอีกครั้ง")
        statusEnum = "revised";

      // ใช้ backend status-filter เมื่อ approved/revised (ข้อมูลเยอะ client-side filter ไม่ครบ)
      const useStatusEndpoint =
        statusEnum === "approved" || statusEnum === "revised";

      // ✅ ดึงข้อมูล LINE UID จาก MemberList API
      const memberLineUidMap = {};

      const processMembersList = (membersList) => {
        membersList.forEach((member) => {
          const memberId = member.memberid || member.id;
          if (memberId) {
            const lineUid =
              member.lineuid ||
              member.line_uid ||
              member.lineUserId ||
              member.profile?.lineuid ||
              member.profile?.line_uid ||
              null;
            if (lineUid) memberLineUidMap[memberId] = lineUid;
          }
        });
      };

      // ✅ ดึง lab list: ถ้า filter approved/revised ใช้ endpoint ใหม่ (backend cross-ref DB)
      //                   ถ้า pending/ไม่ filter ใช้ endpoint เดิม
      const labListParams = { ...apiFilters, pageNow };
      delete labListParams.statusFilter; // ไม่ส่ง statusFilter ไป MFU API

      const [firstLabRaw, firstMembersRes] = await Promise.all([
        useStatusEndpoint
          ? memberAPI.getLabListByStatus({
              ...labListParams,
              status: statusEnum,
            })
          : memberAPI.getLabList(labListParams),
        memberAPI.getList({ ...apiFilters, pageNow }),
      ]);

      // by-status endpoint คืน { status, data: { list, pageAmount } }
      // MFU API คืน { list, pageAmount } ตรงๆ → normalize ให้เหมือนกัน
      const firstLabRes = useStatusEndpoint
        ? firstLabRaw?.data || firstLabRaw
        : firstLabRaw;

      const labPageAmount = firstLabRes.pageAmount || 1;
      setTotalPages(labPageAmount);
      if (
        pageNow === 1 &&
        Array.isArray(firstLabRes.list) &&
        firstLabRes.list.length > 0
      ) {
        setPageSize(firstLabRes.list.length);
      }

      processMembersList(firstMembersRes?.list || []);

      // ✅ ดึง templates ทันที (รันพร้อมกับการ fetch ข้อมูล)
      let allTemplates = [];
      const templatesPromise = templateAPI
        .getAll()
        .then((res) => {
          if (res.status === "success" && res.data) allTemplates = res.data;
        })
        .catch(() => {});

      // Helper: sort และ update state ทันที
      const toTimestamp = (rawDate) => {
        if (!rawDate) return 0;
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) return d.getTime();
        const parts = String(rawDate).split(/[-\/]/);
        if (parts.length === 3 && parts[0].length <= 2) {
          const d2 = new Date(parts[2], parts[1] - 1, parts[0]);
          return isNaN(d2.getTime()) ? 0 : d2.getTime();
        }
        return 0;
      };
      const sortAndUpdate = (records) => {
        // ถ้า fetch ใหม่ถูกเริ่มแล้ว ให้ทิ้งผลของ fetch เก่า
        if (currentFetchId !== fetchIdRef.current) return;
        const sorted = [...records].sort((a, b) => {
          const tA = toTimestamp(a.rawDate);
          const tB = toTimestamp(b.rawDate);
          if (tA !== tB) return tB - tA;
          return a.patientName.localeCompare(b.patientName, "th");
        });
        sorted.forEach((r, i) => {
          r.no = i + 1;
        });
        // set allHealthRecords + healthRecords + loading พร้อมกันใน render เดียว ป้องกัน flash "ไม่พบรายการ"
        setAllHealthRecords([...sorted]);
        setHealthRecords([...sorted]);
        setLoading(false);
      };

      // Deduplication tracking
      const seenKeys = new Set();
      const deduplicateList = (list) =>
        list.filter((r) => {
          const paramKey = Array.isArray(r.data_lab)
            ? r.data_lab
                .map((p) => p.name)
                .sort()
                .join("|")
            : "";
          const key = `${r.memberid}_${r.dateadd}_${paramKey}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });

      let allFormattedData = [];

      // แปลงข้อมูลจาก MFU API ให้ตรงกับรูปแบบที่ UI ใช้ (per batch)
      const formatBatch = async (list) =>
        await Promise.all(
          list.map(async (record, index) => {
            const profile = record.profile || {};
            const dataLab = record.data_lab || [];
            const resolvedHn = profile.hn || record.hn || "";

            // ฟังก์ชันแยกค่าและหน่วยออกจากกัน
            const separateValueAndUnit = (value) => {
              if (!value || typeof value !== "string")
                return { value, unit: "" };

              // รูปแบบที่พบ: "1-2Cells/HPF", "0-1Cells/HPF", "Negative", "Yellow", etc.
              // แยกตัวเลข/ค่า ออกจากหน่วย
              const patterns = [
                { regex: /^(.+?)(Cells\/HPF)$/i, split: true }, // "1-2Cells/HPF" -> "1-2" + "Cells/HPF"
                { regex: /^(.+?)(\/HPF)$/i, split: true }, // "0-5/HPF" -> "0-5" + "/HPF"
                { regex: /^(.+?)(\/LPF)$/i, split: true }, // "0-2/LPF" -> "0-2" + "/LPF"
                { regex: /^(.+?)(cells\/µL)$/i, split: true }, // "100cells/µL" -> "100" + "cells/µL"
              ];

              for (const pattern of patterns) {
                const match = value.match(pattern.regex);
                if (match) {
                  return { value: match[1], unit: match[2] };
                }
              }

              // ถ้าไม่ตรงกับ pattern ใดๆ ให้คืนค่าเดิม
              return { value, unit: "" };
            };

            // แปลง data_lab array เป็น object เพื่อให้เข้าถึงได้ง่าย
            // ⚠️ API อาจส่งค่าซ้ำกัน (เช่น liverfunctiontestalk มา 3 ค่า: ผลแลป, ค่าปกติ, หน่วย)
            // ✅ เก็บเฉพาะค่าแรกที่ไม่ว่างเปล่า
            // ✅ Normalize field name: แปลงเป็นตัวพิมพ์เล็กและลบช่องว่างออก
            const labData = {};
            dataLab.forEach((item, idx) => {
              // แปลงเป็น string ก่อนเสมอ เพื่อรองรับกรณี API ส่งค่าเป็น number (เช่น 108 แทน "108")
              const strValue =
                item.value != null ? String(item.value).trim() : "";
              if (!labData[item.name] && strValue !== "") {
                // เก็บทั้งชื่อเดิมและชื่อที่ normalize แล้ว
                labData[item.name] = strValue;

                // Normalize: ตัวพิมพ์เล็ก + ลบช่องว่าง
                const normalizedKey = item.name
                  .toLowerCase()
                  .replace(/\s+/g, "");
                if (!labData[normalizedKey]) {
                  labData[normalizedKey] = strValue;
                }
                // Normalize: แปลง hyphen → underscore (เช่น stool-color-result → stool_color_result)
                const underscoredKey = item.name
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .replace(/-/g, "_");
                if (!labData[underscoredKey]) {
                  labData[underscoredKey] = strValue;
                }

                if (normalizedKey === "bodyweight" && !labData["weight"]) {
                  labData["weight"] = strValue;
                }

                const aliasKeyMap = {
                  electrolytepotassium: "electrolytepotassiumresult",
                  electrolytechoridere: "electrolytechorideresult",
                  electrolytecalciumre: "electrolytecalciumresult",
                  electrolytemagnesium: "electrolytemagnesiumresult",
                  electrolytephosphoru: "electrolytephosphorusresult",
                  inorganicphosphate: "inorganic_phosphate",
                  electrolytesodiumres: "electrolytesodiumresult",
                  electrolyteco2normal: "electrolyteco2result",
                  liverunctiontesttota: "totalprotein",
                  liverfunctiontestalb: "albumin",
                  liverfunctiontestglo: "globulin",
                  urinalysisrbccastnor: "urinalysisrbccast",
                  urinalysiscalciumpho: "urinalysiscalciumphos",
                  urinalysiscalciumoxalatecrystalresult: "urinalysiscalciumoxa",
                };

                const aliasKey = aliasKeyMap[normalizedKey];
                if (aliasKey && !labData[aliasKey]) {
                  labData[aliasKey] = strValue;
                }

                if (normalizedKey === "liverfunctiontestbil") {
                  if (!labData["bilirubin"]) {
                    labData["bilirubin"] = strValue;
                  } else if (
                    !labData["direct_bilirubin"] &&
                    labData["bilirubin"] !== strValue
                  ) {
                    labData["direct_bilirubin"] = strValue;
                  }
                }
              }
            });

            const birthdayField =
              profile.birthday ||
              profile.birthdate ||
              record.birthday ||
              record.birthdate ||
              profile.birth_date ||
              record.birth_date;

            // ✅ age เป็นค่าละเอียด (ทศนิยมของปี) ใช้จับคู่ช่วงอายุใน medium.json (รองรับเคสทารกแรกเกิด)
            // ส่วน displayAge เป็นปีเต็ม ใช้แสดงผลในสมุดสุขภาพ
            let age = 0;
            if (birthdayField) {
              const calculatedAge = calculatePreciseAgeAtDate(
                birthdayField,
                record.dateadd,
              );

              if (calculatedAge !== null) {
                age = calculatedAge;
              } else if (profile.age) {
                age = parseInt(profile.age);
              }
            } else if (profile.age) {
              age = parseInt(profile.age);
            }
            const displayAge = Number.isFinite(age) ? Math.floor(age) : age;
            const sex = profile.gender === "1" ? "M" : "F";

            // แปลงข้อมูล Lab Results เป็น parameters สำหรับแสดงในตาราง
            const parameters = [];
            const testsNeedingNormalRange = []; // เก็บรายการที่ต้องดึงค่าปกติจาก medium.json

            // CBC - Complete Blood Count
            // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
            if (labData["wbc"] || labData["cbcwbcresult"]) {
              const param = {
                name: "WBC",
                result: labData["wbc"] || labData["cbcwbcresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("WBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "WBC", sex, age });
            }

            if (labData["hb"] || labData["cbchbresult"]) {
              const param = {
                name: "Hemoglobin",
                result: labData["hb"] || labData["cbchbresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Hemoglobin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hemoglobin",
                sex,
                age,
              });
            }

            if (labData["hct"] || labData["cbchctresult"]) {
              const param = {
                name: "Hematocrit",
                result: labData["hct"] || labData["cbchctresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Hematocrit"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hematocrit",
                sex,
                age,
              });
            }

            if (labData["mcv"] || labData["cbcmcvresult"]) {
              const param = {
                name: "MCV",
                result: labData["mcv"] || labData["cbcmcvresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("MCV"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "MCV", sex, age });
            }

            if (labData["mch"] || labData["cbcmchresult"]) {
              const param = {
                name: "MCH",
                result: labData["mch"] || labData["cbcmchresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("MCH"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "MCH", sex, age });
            }

            if (labData["mchc"] || labData["cbcmchcresult"]) {
              const param = {
                name: "MCHC",
                result: labData["mchc"] || labData["cbcmchcresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("MCHC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "MCHC", sex, age });
            }

            if (
              labData["plt"] ||
              labData["cbcpltcountresult"] ||
              labData["cbc plt count result"] ||
              labData["cbc plt count"]
            ) {
              const param = {
                name: "Platelets count",
                result:
                  labData["plt"] ||
                  labData["cbcpltcountresult"] ||
                  labData["cbc plt count result"] ||
                  labData["cbc plt count"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Platelets count"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Platelets count",
                sex,
                age,
              });
            }

            if (labData["cbcpltresult"] || labData["cbcplt"]) {
              const param = {
                name: "Platelet",
                result: labData["cbcpltresult"] || labData["cbcplt"],
                normal: null,
                unit: getUnitFromMaster("Platelet"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Platelet",
                sex,
                age,
              });
            }

            if (labData["neutrophile"] || labData["cbcneutrophileresult"]) {
              const param = {
                name: "Neutrophile",
                result:
                  labData["neutrophile"] || labData["cbcneutrophileresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Neutrophile"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Neutrophile",
                sex,
                age,
              });
            }

            if (labData["lymphocyte_count"] || labData["cbclymphocyteresult"]) {
              const param = {
                name: "Lymphocyte",
                result:
                  labData["lymphocyte_count"] || labData["cbclymphocyteresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Lymphocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphocyte",
                sex,
                age,
              });
            }

            if (labData["monocyte_count"] || labData["cbcmonocyteresult"]) {
              const param = {
                name: "Monocyte",
                result:
                  labData["monocyte_count"] || labData["cbcmonocyteresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Monocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Monocyte",
                sex,
                age,
              });
            }

            if (labData["cbceosinophileresult"]) {
              const param = {
                name: "Eosinophil",
                result: labData["cbceosinophileresult"],
                normal: null,
                unit: getUnitFromMaster("Eosinophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Eosinophil",
                sex,
                age,
              });
            }

            // Kidney Function Tests
            // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
            if (
              labData["creatinine"] ||
              labData["creatininecreatinineresult"]
            ) {
              const param = {
                name: "Creatinine",
                result:
                  labData["creatinine"] ||
                  labData["creatininecreatinineresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Creatinine"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Creatinine",
                sex,
                age,
              });
            }

            if (labData["creatinineegfrresult"] || labData["egfr"]) {
              const param = {
                name: "eGFR",
                result: labData["creatinineegfrresult"] || labData["egfr"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("eGFR"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "eGFR", sex, age });
            }

            if (labData["uric"] || labData["uricacidresult"]) {
              const param = {
                name: "Uric Acid",
                result: labData["uric"] || labData["uricacidresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Uric Acid"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Uric Acid",
                sex,
                age,
              });
            }

            // Diabetes Tests
            // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
            if (
              labData["glucoseresult"] ||
              labData["fbs"] ||
              labData["fbs/fpg"]
            ) {
              const param = {
                name: "Fasting blood glucose",
                result:
                  labData["glucoseresult"] ||
                  labData["fbs"] ||
                  labData["fbs/fpg"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Fasting blood glucose"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Fasting blood glucose",
                sex,
                age,
              });
            }

            if (
              labData["non_npo_glucose"] ||
              labData["sugar_non_npo"] ||
              labData["glucosenonnpcresult"] ||
              labData["nonnpobloodglucose"]
            ) {
              const param = {
                name: "Non-NPO blood glucose",
                result:
                  labData["non_npo_glucose"] ||
                  labData["sugar_non_npo"] ||
                  labData["glucosenonnpcresult"] ||
                  labData["nonnpobloodglucose"],
                normal: null,
                unit: getUnitFromMaster("Non-NPO blood glucose"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Non-NPO blood glucose",
                sex,
                age,
              });
            }

            if (labData["hba1c"] || labData["hba1chba1cresult"]) {
              const param = {
                name: "HbA1C",
                result: labData["hba1c"] || labData["hba1chba1cresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("HbA1C"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HbA1C", sex, age });
            }

            if (
              labData["eag"] ||
              labData["hba1ceagresult"] ||
              labData["hba1ceag"] ||
              labData["hba1c eag result"] ||
              labData["hba1c eag"]
            ) {
              const param = {
                name: "eAG",
                result:
                  labData["eag"] ||
                  labData["hba1ceagresult"] ||
                  labData["hba1ceag"] ||
                  labData["hba1c eag result"] ||
                  labData["hba1c eag"],
                normal: null,
                unit: getUnitFromMaster("eAG"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "eAG", sex, age });
            }

            // Liver Function Tests
            // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
            if (labData["sgot"] || labData["liverfunctiontestsgotastresult"]) {
              const param = {
                name: "SGOT (AST)",
                result:
                  labData["sgot"] || labData["liverfunctiontestsgotastresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("SGOT (AST)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "SGOT (AST)",
                sex,
                age,
              });
            }

            if (
              labData["alp"] ||
              labData["liverfunctiontestalkalinephosphataseresult"]
            ) {
              const param = {
                name: "ALP (Alkaline Phosphatase)",
                result:
                  labData["alp"] ||
                  labData["liverfunctiontestalkalinephosphataseresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("ALP (Alkaline Phosphatase)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "ALP", sex, age });
            }

            if (
              labData["sgptresult"] ||
              labData["sgpt"] ||
              labData["liverfunctiontestsgotaltresult"]
            ) {
              const param = {
                name: "SGPT (ALT)",
                result:
                  labData["sgptresult"] ||
                  labData["sgpt"] ||
                  labData["liverfunctiontestsgotaltresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("SGPT (ALT)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "SGPT (ALT)",
                sex,
                age,
              });
            }

            if (labData["bun"] || labData["bunresult"]) {
              const param = {
                name: "BUN",
                result: labData["bun"] || labData["bunresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("BUN"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "BUN, Urea",
                sex,
                age,
              });
            }

            if (
              labData["bilirubin"] ||
              labData["totalbilirubin"] ||
              labData["liverfunctiontestbilirubintotalresult"]
            ) {
              const param = {
                name: "Total Bilirubin",
                result:
                  labData["bilirubin"] ||
                  labData["totalbilirubin"] ||
                  labData["liverfunctiontestbilirubintotalresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Total Bilirubin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Total Bilirubin",
                sex,
                age,
              });
            }

            if (
              labData["direct_bilirubin"] ||
              labData["directbilirubin"] ||
              labData["liverfunctiontestbilirubindirectresult"]
            ) {
              const param = {
                name: "Direct bilirubin",
                result:
                  labData["direct_bilirubin"] ||
                  labData["directbilirubin"] ||
                  labData["liverfunctiontestbilirubindirectresult"],
                normal: null,
                unit: getUnitFromMaster("Direct bilirubin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Direct bilirubin",
                sex,
                age,
              });
            }

            if (
              labData["albumin"] ||
              labData["liverfunctiontestalbuminresult"]
            ) {
              const param = {
                name: "Albumin",
                result:
                  labData["albumin"] ||
                  labData["liverfunctiontestalbuminresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Albumin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Albumin",
                sex,
                age,
              });
            }

            if (
              labData["protein"] ||
              labData["totalprotein"] ||
              labData["liverfunctiontesttotalproteinresult"] ||
              labData["liverunctiontesttotalproteinresult"]
            ) {
              const param = {
                name: "Total Protein",
                result:
                  labData["protein"] ||
                  labData["totalprotein"] ||
                  labData["liverfunctiontesttotalproteinresult"] ||
                  labData["liverunctiontesttotalproteinresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Total Protein"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Total Protein",
                sex,
                age,
              });
            }

            if (labData["ggt"]) {
              const param = {
                name: "GGT",
                result: labData["ggt"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("GGT"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "GGT", sex, age });
            }

            if (
              labData["globulin"] ||
              labData["liverfunctiontestglobulinresult"]
            ) {
              const param = {
                name: "Globulin",
                result:
                  labData["globulin"] ||
                  labData["liverfunctiontestglobulinresult"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Globulin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Globulin",
                sex,
                age,
              });
            }

            // Lipid Profile
            if (labData["hdl"] || labData["hdlcholesterolresult"]) {
              const param = {
                name: "HDL",
                result: labData["hdl"] || labData["hdlcholesterolresult"],
                normal: null,
                unit: getUnitFromMaster("HDL"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HDL", sex, age });
            }

            if (labData["ldl"] || labData["ldlcholesterolresult"]) {
              const param = {
                name: "LDL",
                result: labData["ldl"] || labData["ldlcholesterolresult"],
                normal: null,
                unit: getUnitFromMaster("LDL"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "LDL", sex, age });
            }

            if (
              labData["chol"] ||
              labData["cholesterol"] ||
              labData["cho"] ||
              labData["cholesterolresult"]
            ) {
              const param = {
                name: "Cholesterol",
                result:
                  labData["chol"] ||
                  labData["cholesterol"] ||
                  labData["cho"] ||
                  labData["cholesterolresult"],
                normal: null,
                unit: getUnitFromMaster("Cholesterol"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "คอเลสเตอรอลรวม",
                sex,
                age,
              });
            }

            // หา triglyceride value จาก labData แบบ dynamic (รองรับทุก key variant)
            const tgResultKey = Object.keys(labData).find(
              (k) => k.includes("triglyceride") && k.includes("result"),
            );
            const tgAnyKey = Object.keys(labData).find(
              (k) =>
                k.includes("triglyceride") &&
                !k.includes("normal") &&
                !k.includes("unit"),
            );
            const tgValue =
              labData["triglycerideresult"] ||
              labData["lipidprofiletriglycerideresult"] ||
              (tgResultKey ? labData[tgResultKey] : null) ||
              labData["lipidprofiletriglyceride"] ||
              labData["triglyceride"] ||
              labData["tg"] ||
              labData["tri"] ||
              (tgAnyKey ? labData[tgAnyKey] : null);

            if (
              labData["triglyceride"] ||
              labData["tri"] ||
              labData["tg"] ||
              labData["triglycerideresult"] ||
              labData["lipidprofiletriglyceride"] ||
              labData["lipidprofiletriglycerideresult"] ||
              tgResultKey ||
              tgAnyKey
            ) {
              const param = {
                name: "Triglyceride",
                result: tgValue,
                normal: null,
                unit: getUnitFromMaster("Triglyceride"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "ไตรกลีเซอไรด์",
                sex,
                age,
              });
            }

            // CBC - Additional
            if (labData["rbc"]) {
              const param = {
                name: "RBC",
                result: labData["rbc"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("RBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "RBC", sex, age });
            }

            // Differential Count (%)
            if (labData["neutrophile_percent"] || labData["neutrophile%"]) {
              const param = {
                name: "Neutrophile",
                result:
                  labData["neutrophile_percent"] || labData["neutrophile%"],
                normal: null,
                unit: getUnitFromMaster("Neutrophile"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Neutrophile",
                sex,
                age,
              });
            }

            if (labData["lymphocyte_percent"] || labData["lymphocyte%"]) {
              const param = {
                name: "Lymphocyte",
                result: labData["lymphocyte_percent"] || labData["lymphocyte%"],
                normal: null,
                unit: getUnitFromMaster("Lymphocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphocyte",
                sex,
                age,
              });
            }

            if (labData["monocyte_percent"] || labData["monocyte%"]) {
              const param = {
                name: "Monocyte",
                result: labData["monocyte_percent"] || labData["monocyte%"],
                normal: null,
                unit: getUnitFromMaster("Monocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Monocyte",
                sex,
                age,
              });
            }

            if (
              labData["eosinophil_percent"] ||
              labData["eosinophile_percent"] ||
              labData["eosinophil%"] ||
              labData["eosinophile%"] ||
              labData["eosinophile"]
            ) {
              const param = {
                name: "Eosinophil",
                result:
                  labData["eosinophil_percent"] ||
                  labData["eosinophile_percent"] ||
                  labData["eosinophil%"] ||
                  labData["eosinophile%"] ||
                  labData["eosinophile"],
                normal: null,
                unit: getUnitFromMaster("Eosinophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Eosinophil",
                sex,
                age,
              });
            }

            if (
              labData["basophil_percent"] ||
              labData["basophil%"] ||
              labData["basophile"]
            ) {
              const param = {
                name: "Basophil",
                result:
                  labData["basophil_percent"] ||
                  labData["basophil%"] ||
                  labData["basophile"],
                normal: null,
                unit: getUnitFromMaster("Basophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Basophil",
                sex,
                age,
              });
            }

            // Urinalysis
            // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
            // ✅ API ใช้ชื่อ field แบบ urinalysisXXXresul (มี 'resul' ไม่ใช่ 'result')

            if (
              labData["urinalysiscolorresult"] ||
              labData["urinalysiscolorresul"]
            ) {
              const param = {
                name: "Urine Color",
                result:
                  labData["urinalysiscolorresult"] ||
                  labData["urinalysiscolorresul"],
                normal: null,
                unit: getUnitFromMaster("Urine Color"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Color", sex, age });
            }

            if (
              labData["urinalysisapperanceresult"] ||
              labData["urinalysisapperancer"]
            ) {
              const param = {
                name: "Urine Appearance",
                result:
                  labData["urinalysisapperanceresult"] ||
                  labData["urinalysisapperancer"],
                normal: null,
                unit: getUnitFromMaster("Urine Appearance"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Appearance",
                sex,
                age,
              });
            }

            if (labData["urinalysisclarityresult"] || labData["clarity"]) {
              const param = {
                name: "Clarity",
                result:
                  labData["urinalysisclarityresult"] || labData["clarity"],
                normal: null,
                unit: getUnitFromMaster("Clarity"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Clarity",
                sex,
                age,
              });
            }

            if (labData["urinalysisphresult"]) {
              const param = {
                name: "Urine pH",
                result: labData["urinalysisphresult"],
                normal: null,
                unit: getUnitFromMaster("Urine pH"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "pH", sex, age });
            }

            if (
              labData["urinalysisspecificgravityresult"] ||
              labData["urinalysisspecificgr"]
            ) {
              const param = {
                name: "Specific Gravity",
                result:
                  labData["urinalysisspecificgravityresult"] ||
                  labData["urinalysisspecificgr"],
                normal: null,
                unit: getUnitFromMaster("Specific Gravity"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Specific Gravity",
                sex,
                age,
              });
            }

            if (
              labData["urinalysissugarresult"] ||
              labData["urinalysissugarresul"]
            ) {
              const param = {
                name: "Urine Glucose",
                result:
                  labData["urinalysissugarresult"] ||
                  labData["urinalysissugarresul"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Urine Glucose"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urine Glucose",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisproteinresult"] ||
              labData["urinalysisproteinres"]
            ) {
              const param = {
                name: "Urine Protein",
                result:
                  labData["urinalysisproteinresult"] ||
                  labData["urinalysisproteinres"],
                normal: null, // บังคับให้ดึงจาก master data
                unit: getUnitFromMaster("Urine Protein"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Protein",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisketoneresult"] ||
              labData["urinalysisketoneresu"]
            ) {
              const param = {
                name: "Urine Ketone",
                result:
                  labData["urinalysisketoneresult"] ||
                  labData["urinalysisketoneresu"],
                normal: null,
                unit: getUnitFromMaster("Urine Ketone"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Ketone", sex, age });
            }

            if (
              labData["urinalysisbloodresult"] ||
              labData["urinalysisbloodresul"]
            ) {
              const param = {
                name: "Urine Blood",
                result:
                  labData["urinalysisbloodresult"] ||
                  labData["urinalysisbloodresul"],
                normal: null,
                unit: getUnitFromMaster("Urine Blood"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Blood", sex, age });
            }

            if (labData["urinalysisrbcresult"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisrbcresult"],
              );
              const param = {
                name: "Urine RBC",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urine RBC",
                sex,
                age,
              });
            }

            if (labData["urinalysiswbcresult"]) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysiswbcresult"],
              );
              const param = {
                name: "Urine WBC",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urine WBC",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisbactcellresult"] ||
              labData["urinalysisbactcellre"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisbactcellresult"] ||
                  labData["urinalysisbactcellre"],
              );
              const param = {
                name: "Urine Bacteria",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Bacteria",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisothersresult"] ||
              labData["urinalysisothers"] ||
              labData["urinalysis others result"] ||
              labData["urinalysis others"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisothersresult"] ||
                  labData["urinalysisothers"] ||
                  labData["urinalysis others result"] ||
                  labData["urinalysis others"],
              );
              const param = {
                name: "Urinalysis Others",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urinalysis Others",
                sex,
                age,
              });
            }

            if (
              labData["urinalysismucusthreadresult"] ||
              labData["urinalysismucusthread"] ||
              labData["urinalysis mucus thread result"] ||
              labData["urinalysis mucus thread"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysismucusthreadresult"] ||
                  labData["urinalysismucusthread"] ||
                  labData["urinalysis mucus thread result"] ||
                  labData["urinalysis mucus thread"],
              );
              const param = {
                name: "Mucus thread",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Mucus thread",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisepithelialcellresult"] ||
              labData["urinalysisepithelial"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisepithelialcellresult"] ||
                  labData["urinalysisepithelial"],
              );
              const param = {
                name: "Epithelial Cells",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Epithelial",
                sex,
                age,
              });
            }

            if (labData["urinalysisleukocyte"] || labData["urine_leukocyte"]) {
              const param = {
                name: "Urine Leukocytes",
                result:
                  labData["urinalysisleukocyte"] || labData["urine_leukocyte"],
                normal: null,
                unit: getUnitFromMaster("Urine Leukocytes"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Leukocytes",
                sex,
                age,
              });
            }

            if (labData["urinalysisnitrite"] || labData["urine_nitrite"]) {
              const param = {
                name: "Urine Nitrite",
                result:
                  labData["urinalysisnitrite"] || labData["urine_nitrite"],
                normal: null,
                unit: getUnitFromMaster("Urine Nitrite"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Nitrite",
                sex,
                age,
              });
            }

            if (labData["urinalysisbilirubin"] || labData["urine_bilirubin"]) {
              const param = {
                name: "Urine Bilirubin",
                result:
                  labData["urinalysisbilirubin"] || labData["urine_bilirubin"],
                normal: null,
                unit: getUnitFromMaster("Urine Bilirubin"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Bilirubin",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisurobilinogen"] ||
              labData["urine_urobilinogen"]
            ) {
              const param = {
                name: "Urobilinogen",
                result:
                  labData["urinalysisurobilinogen"] ||
                  labData["urine_urobilinogen"],
                normal: null,
                unit: getUnitFromMaster("Urobilinogen"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Urobilinogen",
                sex,
                age,
              });
            }

            if (labData["urinalysisrbc"] || labData["urine_rbc"]) {
              const param = {
                name: "RBC (Urine)",
                result: labData["urinalysisrbc"] || labData["urine_rbc"],
                normal: null,
                unit: getUnitFromMaster("RBC (Urine)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "RBC (Urine)",
                sex,
                age,
              });
            }

            if (labData["urinalysiswbc"] || labData["urine_wbc"]) {
              const param = {
                name: "WBC (Urine)",
                result: labData["urinalysiswbc"] || labData["urine_wbc"],
                normal: null,
                unit: getUnitFromMaster("WBC (Urine)"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "WBC (Urine)",
                sex,
                age,
              });
            }

            // Hepatitis Markers
            // ✅ แสดงผล Hepatitis screening tests
            if (labData["hbsag"] || labData["hbsaghbsagresult"]) {
              const param = {
                name: "HBsAg",
                result: labData["hbsag"] || labData["hbsaghbsagresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAg"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HBsAg", sex, age });
            }

            if (
              labData["hbsagconcentrationresult"] ||
              labData["hbsaghbsagconcentrationresult"]
            ) {
              const param = {
                name: "HBsAg concentration",
                result:
                  labData["hbsagconcentrationresult"] ||
                  labData["hbsaghbsagconcentrationresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAg concentration"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HBsAg concentration",
                sex,
                age,
              });
            }

            if (
              labData["hbs_ag_methodology"] ||
              labData["hbs_ag_methodologyresult"] ||
              labData["hbs_ag_methodology_result"] ||
              labData["hbs_ag_methodology result"] ||
              labData["hbsagmethodologyresult"]
            ) {
              const param = {
                name: "HBsAg methodology",
                result:
                  labData["hbs_ag_methodology"] ||
                  labData["hbs_ag_methodologyresult"] ||
                  labData["hbs_ag_methodology_result"] ||
                  labData["hbs_ag_methodology result"] ||
                  labData["hbsagmethodologyresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAg methodology"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HBsAg methodology",
                sex,
                age,
              });
            }

            if (labData["anti-hbs"] || labData["antihbsantihbsresult"]) {
              const param = {
                name: "HBsAb",
                result: labData["anti-hbs"] || labData["antihbsantihbsresult"],
                normal: null,
                unit: getUnitFromMaster("HBsAb"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HBsAb",
                sex,
                age,
              });
            }

            if (
              labData["antihbsantihbsconcen"] ||
              labData["antihbsantihbsconcentrationresult"]
            ) {
              const param = {
                name: "Anti-HBs Concentration",
                result:
                  labData["antihbsantihbsconcen"] ||
                  labData["antihbsantihbsconcentrationresult"],
                normal: null,
                unit: getUnitFromMaster("Anti-HBs Concentration"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Anti-HBs Concentration",
                sex,
                age,
              });
            }

            if (labData["hcvabhcvabresult"]) {
              const param = {
                name: "HCV Ab",
                result: labData["hcvabhcvabresult"],
                normal: null,
                unit: getUnitFromMaster("HCV Ab"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "HCV Ab", sex, age });
            }

            if (
              labData["hcvabcoiresult"] ||
              labData["hcv ab coi result"] ||
              labData["hcvabcoi"] ||
              labData["hcv ab coi"]
            ) {
              const param = {
                name: "HCV Ab COI",
                result:
                  labData["hcvabcoiresult"] ||
                  labData["hcv ab coi result"] ||
                  labData["hcvabcoi"] ||
                  labData["hcv ab coi"],
                normal: null,
                unit: getUnitFromMaster("COI"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HCV Ab COI",
                sex,
                age,
              });
            }

            if (
              labData["hcvabmethodologyresult"] ||
              labData["hcvabmethodology"] ||
              labData["hcv_ab_methodology_result"] ||
              labData["hcv ab methodology result"] ||
              labData["hcv ab methodology"]
            ) {
              const param = {
                name: "HCV Ab methodology",
                result:
                  labData["hcvabmethodologyresult"] ||
                  labData["hcvabmethodology"] ||
                  labData["hcv_ab_methodology_result"] ||
                  labData["hcv ab methodology result"] ||
                  labData["hcv ab methodology"],
                normal: null,
                unit: getUnitFromMaster("HCV Ab methodology"),
                isHepatitis: true,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "HCV Ab methodology",
                sex,
                age,
              });
            }

            // Stool Examination
            // ✅ แปลงค่าผลตรวจอุจจาระจาก English → ไทย
            const translateStoolValue = (val) => {
              if (!val) return val;
              const map = {
                notfound: "ไม่พบ",
                found: "พบ",
                negative: "ไม่พบ",
                positive: "พบ",
                brown: "น้ำตาล",
                lightbrown: "น้ำตาลอ่อน",
                darkbrown: "น้ำตาลเข้ม",
                yellow: "เหลือง",
                green: "เขียว",
                black: "ดำ",
                red: "แดง",
                soft: "นิ่ม",
                hard: "แข็ง",
                liquid: "เหลว",
                formed: "เป็นก้อน",
                semiformed: "กึ่งแข็ง",
                watery: "เหลวมาก",
              };
              return map[val.toLowerCase().replace(/\s+/g, "")] || val;
            };

            // ✅ แสดงผล Stool tests
            if (
              labData["stool_color_result"] ||
              labData["directsmearstoolcolorresult"]
            ) {
              const param = {
                name: "Stool Color",
                result:
                  labData["stool_color_result"] ||
                  labData["directsmearstoolcolorresult"],
                normal: null,
                unit: getUnitFromMaster("Stool Color"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Stool Color",
                sex,
                age,
              });
            }

            if (
              labData["stool_rbc_result"] ||
              labData["directsmearstoolrbcresult"]
            ) {
              const param = {
                name: "Stool RBC",
                result:
                  labData["stool_rbc_result"] ||
                  labData["directsmearstoolrbcresult"],
                normal: null,
                unit: getUnitFromMaster("Stool RBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Stool RBC",
                sex,
                age,
              });
            }

            if (
              labData["stool_wbc_result"] ||
              labData["directsmearstoolwbcresult"]
            ) {
              const param = {
                name: "Stool WBC",
                result:
                  labData["stool_wbc_result"] ||
                  labData["directsmearstoolwbcresult"],
                normal: null,
                unit: getUnitFromMaster("Stool WBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Stool WBC",
                sex,
                age,
              });
            }

            if (
              labData["hemoglobinbdetection"] ||
              labData[
                "hemoglobinbdetectionstoolhemoglobindetectionstoolresult"
              ] ||
              labData["hemoglobindetectionstoolresult"] ||
              labData[
                "hemoglobinb_detection_stool_hemoglobin_detection_stool_result"
              ] ||
              labData["hemoglobin_detection_stool_result"]
            ) {
              const hemoglobinStoolValue =
                labData["hemoglobinbdetection"] ||
                labData[
                  "hemoglobinbdetectionstoolhemoglobindetectionstoolresult"
                ] ||
                labData["hemoglobindetectionstoolresult"] ||
                labData[
                  "hemoglobinb_detection_stool_hemoglobin_detection_stool_result"
                ] ||
                labData["hemoglobin_detection_stool_result"];
              const param = {
                name: "Hemoglobin detection stool",
                result: hemoglobinStoolValue,
                normal: null,
                unit: getUnitFromMaster("Hemoglobin detection stool"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hemoglobin detection stool",
                sex,
                age,
              });
            }

            if (labData["stool_parasite_result"] || labData["parasite"]) {
              const param = {
                name: "Parasite",
                result: labData["stool_parasite_result"] || labData["parasite"],
                normal: null,
                unit: getUnitFromMaster("Parasite"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Parasite",
                sex,
                age,
              });
            }

            if (
              labData["stool_consistency_result"] ||
              labData["consistency"] ||
              labData["directsmearstoolconsistencyresult"]
            ) {
              const param = {
                name: "Consistency",
                result:
                  labData["stool_consistency_result"] ||
                  labData["consistency"] ||
                  labData["directsmearstoolconsistencyresult"],
                normal: null,
                unit: getUnitFromMaster("Consistency"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Consistency",
                sex,
                age,
              });
            }

            // CBC - Additional Tests
            // ✅ เพิ่ม CBC tests ที่ขาดหายไป
            if (
              labData["basophil_count"] ||
              labData["basophil-count"] ||
              labData["cbcbasophileresult"]
            ) {
              const param = {
                name: "Basophil",
                result:
                  labData["basophil_count"] ||
                  labData["basophil-count"] ||
                  labData["cbcbasophileresult"],
                normal: null,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Basophil",
                sex,
                age,
              });
            }

            // ✅ CBC additional / morphology fields
            if (labData["cbcnrbcresult"] || labData["nrbc"]) {
              const param = {
                name: "NRBC",
                result: labData["cbcnrbcresult"] || labData["nrbc"],
                normal: null,
                unit: getUnitFromMaster("NRBC"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "NRBC", sex, age });
            }

            if (
              labData["cbcatypicallymphocyteresult"] ||
              labData["cbcatypicallymphocyte"] ||
              labData["cbc_atypical_lymphocyte_result"] ||
              labData["cbc_atypical_lymphocyte"] ||
              labData["cbc_atypical_lymphoc"]
            ) {
              const param = {
                name: "Atypical Lymphocyte",
                result:
                  labData["cbcatypicallymphocyteresult"] ||
                  labData["cbcatypicallymphocyte"] ||
                  labData["cbc_atypical_lymphocyte_result"] ||
                  labData["cbc_atypical_lymphocyte"] ||
                  labData["cbc_atypical_lymphoc"],
                normal: null,
                unit: getUnitFromMaster("Atypical Lymphocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Atypical Lymphocyte",
                sex,
                age,
              });
            }

            if (
              labData["cbcmyeloblastresult"] ||
              labData["cbcmyeloblast"] ||
              labData["cbc_myeloblast_result"] ||
              labData["cbc_myeloblast"]
            ) {
              const param = {
                name: "Myeloblast",
                result:
                  labData["cbcmyeloblastresult"] ||
                  labData["cbcmyeloblast"] ||
                  labData["cbc_myeloblast_result"] ||
                  labData["cbc_myeloblast"],
                normal: null,
                unit: getUnitFromMaster("Myeloblast"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Myeloblast",
                sex,
                age,
              });
            }

            if (
              labData["cbcpromyelocyteresult"] ||
              labData["cbcpromyelocyte"] ||
              labData["cbc_promyelocyte_result"] ||
              labData["cbc_promyelocyte"]
            ) {
              const param = {
                name: "Promyelocyte",
                result:
                  labData["cbcpromyelocyteresult"] ||
                  labData["cbcpromyelocyte"] ||
                  labData["cbc_promyelocyte_result"] ||
                  labData["cbc_promyelocyte"],
                normal: null,
                unit: getUnitFromMaster("Promyelocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Promyelocyte",
                sex,
                age,
              });
            }

            if (
              labData["cbcmyelocyteresult"] ||
              labData["cbcmyelocyte"] ||
              labData["cbc_myelocyte_result"] ||
              labData["cbc_myelocyte"]
            ) {
              const param = {
                name: "Myelocyte",
                result:
                  labData["cbcmyelocyteresult"] ||
                  labData["cbcmyelocyte"] ||
                  labData["cbc_myelocyte_result"] ||
                  labData["cbc_myelocyte"],
                normal: null,
                unit: getUnitFromMaster("Myelocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Myelocyte",
                sex,
                age,
              });
            }

            if (
              labData["cbcmetamyelocyteresult"] ||
              labData["cbcmetamyelocyte"] ||
              labData["cbc_metamyelocyte_result"] ||
              labData["cbc_metamyelocyte"]
            ) {
              const param = {
                name: "Metamyelocyte",
                result:
                  labData["cbcmetamyelocyteresult"] ||
                  labData["cbcmetamyelocyte"] ||
                  labData["cbc_metamyelocyte_result"] ||
                  labData["cbc_metamyelocyte"],
                normal: null,
                unit: getUnitFromMaster("Metamyelocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Metamyelocyte",
                sex,
                age,
              });
            }

            if (
              labData["cbclymphoblastresult"] ||
              labData["cbclymphoblast"] ||
              labData["cbc_lymphoblast_result"] ||
              labData["cbc_lymphoblast"]
            ) {
              const param = {
                name: "Lymphoblast",
                result:
                  labData["cbclymphoblastresult"] ||
                  labData["cbclymphoblast"] ||
                  labData["cbc_lymphoblast_result"] ||
                  labData["cbc_lymphoblast"],
                normal: null,
                unit: getUnitFromMaster("Lymphoblast"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphoblast",
                sex,
                age,
              });
            }

            if (labData["cbcbandformresult"]) {
              const param = {
                name: "Band form",
                result: labData["cbcbandformresult"],
                normal: null,
                unit: getUnitFromMaster("Band form"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Band form",
                sex,
                age,
              });
            }

            if (labData["cbcanisocytosisresult"]) {
              const param = {
                name: "Anisocytosis",
                result: labData["cbcanisocytosisresult"],
                normal: null,
                unit: getUnitFromMaster("Anisocytosis"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Anisocytosis",
                sex,
                age,
              });
            }

            if (
              labData["cbchypochromicresult"] ||
              labData["cbchypochromic"] ||
              labData["cbc hypochromic result"] ||
              labData["cbc hypochromic"]
            ) {
              const param = {
                name: "Hypochromic",
                result:
                  labData["cbchypochromicresult"] ||
                  labData["cbchypochromic"] ||
                  labData["cbc hypochromic result"] ||
                  labData["cbc hypochromic"],
                normal: null,
                unit: getUnitFromMaster("Hypochromic"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hypochromic",
                sex,
                age,
              });
            }

            if (labData["cbcmicrocyteresult"] || labData["cbcmicrocyte"]) {
              const param = {
                name: "Microcyte",
                result:
                  labData["cbcmicrocyteresult"] || labData["cbcmicrocyte"],
                normal: null,
                unit: getUnitFromMaster("Microcyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Microcyte",
                sex,
                age,
              });
            }

            if (labData["cbcmacrocyteresult"]) {
              const param = {
                name: "Macrocyte",
                result: labData["cbcmacrocyteresult"],
                normal: null,
                unit: getUnitFromMaster("Macrocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Macrocyte",
                sex,
                age,
              });
            }

            if (labData["cbcotherresult"] || labData["cbcother"]) {
              const param = {
                name: "Others",
                result: labData["cbcotherresult"] || labData["cbcother"],
                normal: null,
                unit: getUnitFromMaster("Others"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Others",
                sex,
                age,
              });
            }

            if (labData["cbcpoikilocytosisresult"]) {
              const param = {
                name: "Poikilocytosis",
                result: labData["cbcpoikilocytosisresult"],
                normal: null,
                unit: getUnitFromMaster("Poikilocytosis"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Poikilocytosis",
                sex,
                age,
              });
            }

            if (labData["cbcovalocyteresult"]) {
              const param = {
                name: "Ovalocyte",
                result: labData["cbcovalocyteresult"],
                normal: null,
                unit: getUnitFromMaster("Ovalocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Ovalocyte",
                sex,
                age,
              });
            }

            if (labData["cbctargetcellresult"]) {
              const param = {
                name: "Target cell",
                result: labData["cbctargetcellresult"],
                normal: null,
                unit: getUnitFromMaster("Target cell"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Target cell",
                sex,
                age,
              });
            }

            if (labData["cbcschistocyteresult"]) {
              const param = {
                name: "Schistocyte",
                result: labData["cbcschistocyteresult"],
                normal: null,
                unit: getUnitFromMaster("Schistocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Schistocyte",
                sex,
                age,
              });
            }

            if (
              labData["cbcspherocyteresult"] ||
              labData["cbcspherocyte"] ||
              labData["cbc_spherocyte_result"] ||
              labData["cbc_spherocyte"]
            ) {
              const param = {
                name: "Spherocyte",
                result:
                  labData["cbcspherocyteresult"] ||
                  labData["cbcspherocyte"] ||
                  labData["cbc_spherocyte_result"] ||
                  labData["cbc_spherocyte"],
                normal: null,
                unit: getUnitFromMaster("Spherocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Spherocyte",
                sex,
                age,
              });
            }

            if (labData["cbcteardropresult"]) {
              const param = {
                name: "Tear drop cell",
                result: labData["cbcteardropresult"],
                normal: null,
                unit: getUnitFromMaster("Tear drop cell"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Tear drop cell",
                sex,
                age,
              });
            }

            if (labData["cbcburrcellresult"]) {
              const param = {
                name: "Burr cell",
                result: labData["cbcburrcellresult"],
                normal: null,
                unit: getUnitFromMaster("Burr cell"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Burr cell",
                sex,
                age,
              });
            }

            if (labData["cbcpolychromasiaresult"]) {
              const param = {
                name: "Polychromasia",
                result: labData["cbcpolychromasiaresult"],
                normal: null,
                unit: getUnitFromMaster("Polychromasia"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Polychromasia",
                sex,
                age,
              });
            }

            if (labData["cbcblastresult"]) {
              const param = {
                name: "Blast cell",
                result: labData["cbcblastresult"],
                normal: null,
                unit: getUnitFromMaster("Blast cell"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Blast cell",
                sex,
                age,
              });
            }

            if (labData["lymphocyte-count"] && !labData["lymphocyte_count"]) {
              const param = {
                name: "Lymphocyte",
                result: labData["lymphocyte-count"],
                normal: null,
                unit: getUnitFromMaster("Lymphocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Lymphocyte",
                sex,
                age,
              });
            }

            if (labData["monocyte-count"] && !labData["monocyte_count"]) {
              const param = {
                name: "Monocyte",
                result: labData["monocyte-count"],
                normal: null,
                unit: getUnitFromMaster("Monocyte"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Monocyte",
                sex,
                age,
              });
            }

            // BMI
            if (labData["bmi"]) {
              const param = {
                name: "BMI",
                result: labData["bmi"],
                normal: null,
                unit: "kg/m²",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "BMI", sex, age });
            }

            // แตก sys/dias combined field เช่น "138/92" → ["138", "92"]
            const sysDiasRaw =
              labData["sys/dias"] ||
              record["sys/dias"] ||
              profile["sys/dias"] ||
              "";
            const sysDiasParts =
              sysDiasRaw && sysDiasRaw.includes("/")
                ? sysDiasRaw.split("/").map((v) => v.trim())
                : [];

            // ความดันบน (Systolic)
            const bpsValue =
              labData["bps"] ||
              labData["sys"] ||
              record.bps ||
              record.sys ||
              record.systolic_bp ||
              profile.bps ||
              profile.sys ||
              profile.systolic_bp ||
              sysDiasParts[0];
            if (bpsValue) {
              const param = {
                name: "BPS",
                result: String(bpsValue),
                normal: null,
                unit: "mmHg",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "BPS", sex, age });
            }

            // ความดันล่าง (Diastolic)
            const bpdValue =
              labData["bpd"] ||
              labData["dia"] ||
              record.bpd ||
              record.dia ||
              record.diastolic_bp ||
              profile.bpd ||
              profile.dia ||
              profile.diastolic_bp ||
              profile.dias ||
              sysDiasParts[1];
            if (bpdValue) {
              const param = {
                name: "BPD",
                result: String(bpdValue),
                normal: null,
                unit: "mmHg",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "BPD", sex, age });
            }

            // ชีพจร (Pulse)
            const pulseValue =
              labData["pulse"] || record.pulse || profile.pulse;
            if (pulseValue) {
              const param = {
                name: "Pulse",
                result: String(pulseValue),
                normal: null,
                unit: "ครั้ง/นาที",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "Pulse", sex, age });
            }

            // กรุ๊ปเลือด (Blood Group) - ดึงจากผล ABO Cell Grouping
            const bloodGroupValue =
              labData["abocellgrouping"] ||
              labData["abo_cell_grouping"] ||
              labData["abo cell grouping"] ||
              record.bloodgroup ||
              profile.bloodgroup;
            if (bloodGroupValue) {
              const param = {
                name: "กรุ๊ปเลือด",
                result: String(bloodGroupValue),
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "กรุ๊ปเลือด",
                sex,
                age,
              });
            }

            // ✅ Urinalysis - Casts
            if (
              labData["urinalysishyalinecastresult"] ||
              labData["urinalysishyalinecas"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysishyalinecastresult"] ||
                  labData["urinalysishyalinecas"],
              );
              const param = {
                name: "Hyaline cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Hyaline cast",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisgranularcastresult"] ||
              labData["urinalysisgranularca"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisgranularcastresult"] ||
                  labData["urinalysisgranularca"],
              );
              const param = {
                name: "Granular cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Granular cast",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiscellularcastresult"] ||
              labData["urinalysiscellularca"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysiscellularcastresult"] ||
                  labData["urinalysiscellularca"],
              );
              const param = {
                name: "Cellular cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Cellular cast",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiswaxycastresult"] ||
              labData["urinalysiswaxycastno"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysiswaxycastresult"] ||
                  labData["urinalysiswaxycastno"],
              );
              const param = {
                name: "Waxy cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Waxy cast",
                sex,
                age,
              });
            }

            // ✅ Urinalysis - Crystals
            if (
              labData["urinalysiscalciumoxalatecrystalresult"] ||
              labData["urinalysiscalciumoxa"]
            ) {
              const param = {
                name: "Calcium oxalate crystal",
                result:
                  labData["urinalysiscalciumoxalatecrystalresult"] ||
                  labData["urinalysiscalciumoxa"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Calcium oxalate crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiscalciumcarbonatecrystalresult"] ||
              labData["urinalysiscalciumcar"] ||
              labData["urinalysis calcium carbonate result"]
            ) {
              const param = {
                name: "Calcium Carbonate crystal",
                result:
                  labData["urinalysiscalciumcarbonatecrystalresult"] ||
                  labData["urinalysiscalciumcar"] ||
                  labData["urinalysis calcium carbonate result"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Calcium Carbonate crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisuricacidcrystalresult"] ||
              labData["urinalysisuricacidcr"] ||
              labData["urinalysis uric acid crystal result"] ||
              labData["uric acid crystal result"]
            ) {
              const param = {
                name: "Uric acid crystal",
                result:
                  labData["urinalysisuricacidcrystalresult"] ||
                  labData["urinalysisuricacidcr"] ||
                  labData["urinalysis uric acid crystal result"] ||
                  labData["uric acid crystal result"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Uric acid crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysistriplephosphatecrystalresult"] ||
              labData["urinalysistriplephos"]
            ) {
              const param = {
                name: "Triple phosphate crystal",
                result:
                  labData["urinalysistriplephosphatecrystalresult"] ||
                  labData["urinalysistriplephos"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Triple phosphate crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiscalciumphosphatecrystalresult"] ||
              labData["urinalysiscalciumphos"] ||
              labData["calcium_phosphate"] ||
              labData["urinalysis calcium phosphate result"]
            ) {
              const param = {
                name: "Calcium phosphate crystal",
                result:
                  labData["urinalysiscalciumphosphatecrystalresult"] ||
                  labData["urinalysiscalciumphos"] ||
                  labData["calcium_phosphate"] ||
                  labData["urinalysis calcium phosphate result"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Calcium phosphate crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysiscystinecrystalresult"] ||
              labData["urinalysiscystinecrystal"] ||
              labData["urinalysiscystinecryst"] ||
              labData["cystine"]
            ) {
              const param = {
                name: "Cystine crystal",
                result:
                  labData["urinalysiscystinecrystalresult"] ||
                  labData["urinalysiscystinecrystal"] ||
                  labData["urinalysiscystinecryst"] ||
                  labData["cystine"],
                normal: null,
                unit: "",
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Cystine crystal",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisrbccastresult"] ||
              labData["urinalysisrbccast"] ||
              labData["rbc_cast"]
            ) {
              const { value, unit } = separateValueAndUnit(
                labData["urinalysisrbccastresult"] ||
                  labData["urinalysisrbccast"] ||
                  labData["rbc_cast"],
              );
              const param = {
                name: "RBC cast",
                result: value,
                normal: null,
                unit: unit,
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "RBC cast",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisnonsquamousepithelialcellresult"] ||
              labData["urinalysisnonsquamousepithelialcell"]
            ) {
              const param = {
                name: "Non-squamous epithelial cell",
                result:
                  labData["urinalysisnonsquamousepithelialcellresult"] ||
                  labData["urinalysisnonsquamousepithelialcell"],
                normal: null,
                unit: getUnitFromMaster("Non-squamous epithelial cell"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Non-squamous epithelial cell",
                sex,
                age,
              });
            }

            if (
              labData["urinalysisrenaltubularepithelialcellresult"] ||
              labData["urinalysisrenaltubularepithelialcell"]
            ) {
              const param = {
                name: "Renal tubular epithelial cell",
                result:
                  labData["urinalysisrenaltubularepithelialcellresult"] ||
                  labData["urinalysisrenaltubularepithelialcell"],
                normal: null,
                unit: getUnitFromMaster("Renal tubular epithelial cell"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Renal tubular epithelial cell",
                sex,
                age,
              });
            }

            if (
              labData["cbcrbcmorphologyresult"] ||
              labData["cbcrbcmorphology"]
            ) {
              const param = {
                name: "RBC morphology",
                result:
                  labData["cbcrbcmorphologyresult"] ||
                  labData["cbcrbcmorphology"],
                normal: null,
                unit: getUnitFromMaster("RBC morphology"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "RBC morphology",
                sex,
                age,
              });
            }

            // RBC morphology now surfaces when present so abnormal morphology can be reviewed.

            // ✅ Electrolytes (เกลือแร่ในเลือด)
            const getLabValue = (...keys) => {
              for (const key of keys) {
                const value = labData[key];
                if (value !== undefined && value !== null && value !== "") {
                  return value;
                }
              }
              return "";
            };

            if (
              labData["sodium"] ||
              labData["na"] ||
              labData["electrolytesodiumresult"] ||
              labData["electrolyteSodiumresult"]
            ) {
              const param = {
                name: "Sodium",
                result:
                  labData["sodium"] ||
                  labData["na"] ||
                  getLabValue(
                    "electrolytesodiumresult",
                    "electrolyteSodiumresult",
                  ),
                normal: null,
                unit: getUnitFromMaster("Sodium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Sodium",
                sex,
                age,
              });
            }

            if (
              labData["potassium"] ||
              labData["k"] ||
              labData["electrolytepotassiumresult"] ||
              labData["electrolytePotassiumresult"]
            ) {
              const param = {
                name: "Potassium",
                result:
                  labData["potassium"] ||
                  labData["k"] ||
                  getLabValue(
                    "electrolytepotassiumresult",
                    "electrolytePotassiumresult",
                  ),
                normal: null,
                unit: getUnitFromMaster("Potassium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Potassium",
                sex,
                age,
              });
            }

            if (
              labData["chloride"] ||
              labData["cl"] ||
              labData["electrolyteChorideresult"] ||
              labData["electrolytechorideresult"] ||
              labData["electrolyteChlorideresult"]
            ) {
              const param = {
                name: "Chloride",
                result:
                  labData["chloride"] ||
                  labData["cl"] ||
                  getLabValue(
                    "electrolyteChorideresult",
                    "electrolytechorideresult",
                    "electrolyteChlorideresult",
                  ),
                normal: null,
                unit: getUnitFromMaster("Chloride"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Chloride",
                sex,
                age,
              });
            }

            if (
              labData["bicarbonate"] ||
              labData["hco3"] ||
              labData["electrolyteco2result"] ||
              labData["electrolyteCo2result"]
            ) {
              const param = {
                name: "Bicarbonate",
                result:
                  labData["bicarbonate"] ||
                  labData["hco3"] ||
                  getLabValue("electrolyteco2result", "electrolyteCo2result"),
                normal: null,
                unit: getUnitFromMaster("Bicarbonate"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Bicarbonate",
                sex,
                age,
              });
            }

            if (
              labData["calcium"] ||
              labData["ca"] ||
              labData["total_calcium"] ||
              labData["electrolytecalciumresult"] ||
              labData["electrolyteCalciumresult"]
            ) {
              const param = {
                name: "Total Calcium",
                result:
                  labData["calcium"] ||
                  labData["ca"] ||
                  labData["total_calcium"] ||
                  getLabValue(
                    "electrolytecalciumresult",
                    "electrolyteCalciumresult",
                  ),
                normal: null,
                unit: getUnitFromMaster("Total Calcium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Total Calcium",
                sex,
                age,
              });
            }

            if (
              labData["phosphate"] ||
              labData["inorganic_phosphate"] ||
              labData["inorganicphosphate"] ||
              labData["electrolytephosphorusresult"] ||
              labData["electrolytePhosphorusresult"]
            ) {
              const param = {
                name: "Inorganic phosphate",
                result:
                  labData["phosphate"] ||
                  labData["inorganic_phosphate"] ||
                  labData["inorganicphosphate"] ||
                  getLabValue(
                    "electrolytephosphorusresult",
                    "electrolytePhosphorusresult",
                  ),
                normal: null,
                unit: getUnitFromMaster("Inorganic phosphate"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Inorganic phosphate",
                sex,
                age,
              });
            }

            if (
              labData["magnesium"] ||
              labData["mg"] ||
              labData["electrolytemagnesiumresult"] ||
              labData["electrolyteMagnesiumresult"]
            ) {
              const param = {
                name: "Magnesium",
                result:
                  labData["magnesium"] ||
                  labData["mg"] ||
                  getLabValue(
                    "electrolytemagnesiumresult",
                    "electrolyteMagnesiumresult",
                  ),
                normal: null,
                unit: getUnitFromMaster("Magnesium"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Magnesium",
                sex,
                age,
              });
            }

            // ✅ Tumor Markers (ค่าบ่งชี้มะเร็ง)
            if (labData["afp"] || labData["tumormarkerafpresult"]) {
              const param = {
                name: "AFP",
                result: labData["afp"] || labData["tumormarkerafpresult"],
                normal: null,
                unit: getUnitFromMaster("AFP"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "AFP",
                sex,
                age,
              });
            }

            if (labData["psa"] || labData["tumormarkerpsaresult"]) {
              const param = {
                name: "PSA",
                result: labData["psa"] || labData["tumormarkerpsaresult"],
                normal: null,
                unit: getUnitFromMaster("PSA"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "PSA",
                sex,
                age,
              });
            }

            if (labData["cea"] || labData["tumormarkercearesult"]) {
              const param = {
                name: "CEA",
                result: labData["cea"] || labData["tumormarkercearesult"],
                normal: null,
                unit: getUnitFromMaster("CEA"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "CEA",
                sex,
                age,
              });
            }

            if (
              labData["ca19_9"] ||
              labData["ca199"] ||
              labData["ca19-9"] ||
              labData["ca19_9result"] ||
              labData["tumormarkerca199resu"] ||
              labData["tumormarkerca19_9resu"] ||
              labData["tumormarkerca199result"]
            ) {
              const param = {
                name: "CA19-9",
                result:
                  labData["ca19_9"] ||
                  labData["ca199"] ||
                  labData["ca19-9"] ||
                  labData["ca19_9result"] ||
                  labData["tumormarkerca199resu"] ||
                  labData["tumormarkerca19_9resu"] ||
                  labData["tumormarkerca199result"],
                normal: null,
                unit: getUnitFromMaster("CA19-9"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "CA19-9",
                sex,
                age,
              });
            }

            if (
              labData["ca125"] ||
              labData["ca125result"] ||
              labData["tumormarkerca125resu"] ||
              labData["tumormarkerca125result"]
            ) {
              const param = {
                name: "CA125",
                result:
                  labData["ca125"] ||
                  labData["ca125result"] ||
                  labData["tumormarkerca125resu"] ||
                  labData["tumormarkerca125result"],
                normal: null,
                unit: getUnitFromMaster("CA125"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "CA125",
                sex,
                age,
              });
            }

            if (
              labData["ca153"] ||
              labData["ca153result"] ||
              labData["ca15_3"] ||
              labData["ca15_3result"] ||
              labData["tumormarkerca153resu"] ||
              labData["tumormarkerca153result"]
            ) {
              const param = {
                name: "CA153",
                result:
                  labData["ca153"] ||
                  labData["ca153result"] ||
                  labData["ca15_3"] ||
                  labData["ca15_3result"] ||
                  labData["tumormarkerca153resu"] ||
                  labData["tumormarkerca153result"],
                normal: null,
                unit: getUnitFromMaster("CA153"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({ param, name: "CA153", sex, age });
            }

            // ✅ Thyroid Hormones (ฮอร์โมนไทรอยด์)
            if (
              labData["free_t3"] ||
              labData["ft3"] ||
              labData["freet3"] ||
              labData["freet3result"] ||
              labData["free_t3_result"] ||
              labData["ft3result"]
            ) {
              const param = {
                name: "Free T3",
                result:
                  labData["free_t3"] ||
                  labData["ft3"] ||
                  labData["freet3"] ||
                  labData["freet3result"] ||
                  labData["free_t3_result"] ||
                  labData["ft3result"],
                normal: null,
                unit: getUnitFromMaster("Free T3"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Free T3",
                sex,
                age,
              });
            }

            if (
              labData["free_t4"] ||
              labData["ft4"] ||
              labData["freet4"] ||
              labData["freet4result"]
            ) {
              const param = {
                name: "Free T4",
                result:
                  labData["free_t4"] ||
                  labData["ft4"] ||
                  labData["freet4"] ||
                  labData["freet4result"],
                normal: null,
                unit: getUnitFromMaster("Free T4"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Free T4",
                sex,
                age,
              });
            }

            if (labData["tsh"] || labData["tshresult"]) {
              const param = {
                name: "TSH",
                result: labData["tsh"] || labData["tshresult"],
                normal: null,
                unit: getUnitFromMaster("TSH"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "TSH",
                sex,
                age,
              });
            }

            // ✅ Other Tests
            if (
              labData["cholinesterase"] ||
              labData["totalcholinesteraseresult"] ||
              labData["totalcholinesteraser"]
            ) {
              const param = {
                name: "Cholinesterase",
                result:
                  labData["cholinesterase"] ||
                  labData["totalcholinesteraseresult"] ||
                  labData["totalcholinesteraser"],
                normal: null,
                unit: getUnitFromMaster("Cholinesterase"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Cholinesterase",
                sex,
                age,
              });
            }

            if (labData["eosinophil_count"] || labData["eosinophil-count"]) {
              const param = {
                name: "Eosinophil",
                result:
                  labData["eosinophil_count"] || labData["eosinophil-count"],
                normal: null,
                unit: getUnitFromMaster("Eosinophil"),
              };
              parameters.push(param);
              testsNeedingNormalRange.push({
                param,
                name: "Eosinophil",
                sex,
                age,
              });
            }

            // ✅ ดึงค่าปกติและหน่วยจาก master data (medium.json) สำหรับทุก parameters
            if (testsNeedingNormalRange.length > 0 && masterData) {
              testsNeedingNormalRange.forEach((test) => {
                const normalRange = getNormalRangeFromMaster(
                  test.name,
                  test.sex,
                  test.age,
                );
                const unit = getUnitFromMaster(test.name, test.sex, test.age);
                if (test.param) {
                  test.param.normal = normalRange;
                  test.param.unit = unit;
                  // ✅ ปรับค่าผลตรวจให้ตรงกับหลักของค่าปกติ
                  test.param.result = normalizeResultValue(
                    test.param.result,
                    normalRange,
                    test.name,
                  );
                }
              });
            }

            // ใส่ค่า default '-' สำหรับ parameters ที่ยังไม่มีค่าปกติ และ '' สำหรับหน่วย
            parameters.forEach((param) => {
              if (!param.normal) {
                param.normal = "-";
              }
              if (!param.unit) {
                param.unit = "";
              }
            });

            // ✅ ใช้ normalizer กลางเพื่อกรอง parameters แบบเดียวกับ report
            const filteredParameters = getUnifiedParameters(
              { parameters },
              masterData,
            );

            // ใช้ filteredParameters แทน parameters
            const finalParameters = filteredParameters;

            // แปลงวันที่จาก YYYY-MM-DD HH:MM:SS เป็น DD-MM-YYYY
            const formatDate = (dateString) => {
              if (!dateString || dateString === "-") return "-";
              // ถ้าเป็นรูปแบบ "YYYY-MM-DD HH:MM:SS" ให้ตัดเอาแค่วันที่
              const datePart = dateString.split(" ")[0];
              const parts = datePart.split("-");
              if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
              }
              return dateString;
            };

            // โหลด approval_status และคำแนะนำจาก health_recommendations (เหมือน Super Admin)
            let approvalStatus = "pending";
            let approvedAt = null;
            let approvedBy = null;
            let nutritionAdvice = "";
            let exerciseAdvice = "";
            let sleepAdvice = "";
            let doctorAdvice = "";

            let dbRecordId = null;

            // ✅ ดึง dbRecordId จาก health_records table โดยระบุ visitDate+Time ด้วย
            // ต้องส่ง full datetime เพื่อแยก record ที่ตรวจหลายครั้งในวันเดียวกัน
            const visitDateFull = record.dateadd || null;
            try {
              const healthRecordResponse =
                await healthRecordsAPI.getRecordIdByIdentifier(
                  profile.hn,
                  visitDateFull,
                );
              if (
                healthRecordResponse.status === "success" &&
                healthRecordResponse.data?.record_id
              ) {
                dbRecordId = healthRecordResponse.data.record_id;
              }
            } catch (err) {
              // No health record found yet
            }

            // ดึงข้อมูล recommendations และ advice
            // ✅ เรียกเฉพาะเมื่อมี dbRecordId จริงเท่านั้น เพื่อไม่ให้ผูก approval ของ visit อื่น
            if (dbRecordId) {
              try {
                const recResponse =
                  await healthRecommendationAPI.getDraft(dbRecordId);
                if (recResponse.status === "success" && recResponse.data) {
                  approvalStatus =
                    recResponse.data.approval_status || "pending";
                  approvedAt = recResponse.data.approved_at;
                  approvedBy = recResponse.data.approved_by || null;
                  nutritionAdvice = recResponse.data.nutrition_advice || "";
                  exerciseAdvice = recResponse.data.exercise_advice || "";
                  sleepAdvice = recResponse.data.sleep_advice || "";
                  doctorAdvice = recResponse.data.doctor_advice || "";
                }
              } catch (err) {
                // No recommendations yet
              }
            }

            return {
              id: `${record.memberid}_${record.dateadd}`, // unique per member+date (คนเดิมตรวจหลายครั้ง)
              memberid: record.memberid, // เก็บ memberid แยกสำหรับ API calls
              dbRecordId: dbRecordId,
              no: index + 1,
              date: formatDate(record.dateadd),
              rawDate: record.dateadd || "",
              birthday: birthdayField || "",
              patientName:
                `${profile.nameprefix || ""} ${profile.name || ""} ${
                  profile.lastname || ""
                }`.trim() || "-",
              gender: profile.gender === "1" ? "ชาย" : "หญิง",
              age: displayAge || "-",
              diseases: profile.congenitaldisease || "ไม่มี",
              company: record.campaignname || labData["company"] || "-",
              foodAllergies:
                labData["food_allergies"] || labData["food allergies"] || "-",
              hn: profile.hn || "-", // ใช้ hn จาก profile (MemberList API)
              nid: profile.nid || profile.idCard || null, // ✅ NID สำหรับ MFU User App lookup
              line_uid: memberLineUidMap[record.memberid] || null, // ✅ LINE User ID สำหรับส่งการแจ้งเตือน
              parameters: finalParameters, // ✅ ใช้ finalParameters ที่กรองแล้ว (ซ่อน negative parameters)
              templates: [],
              matchedTemplates: [],
              tags: [],
              approvalStatus: approvalStatus,
              approvedAt: approvedAt,
              approvedBy: approvedBy,
              isApproved: approvalStatus === "approved",
              // ข้อมูลเพิ่มเติมสำหรับ PDF
              labData: labData, // ข้อมูล lab ทั้งหมด
              weight: labData["weight"] || "-",
              height: labData["height"] || "-",
              bmi: labData["bmi"] || "-",
              bps:
                labData["bps"] ||
                labData["sys"] ||
                record.bps ||
                record.sys ||
                record.systolic_bp ||
                profile.bps ||
                profile.sys ||
                profile.systolic_bp ||
                (labData["sys/dias"] || "").split("/")[0]?.trim() ||
                "-",
              bpd:
                labData["bpd"] ||
                labData["dia"] ||
                record.bpd ||
                record.dia ||
                record.diastolic_bp ||
                profile.bpd ||
                profile.dia ||
                profile.diastolic_bp ||
                profile.dias ||
                (labData["sys/dias"] || "").split("/")[1]?.trim() ||
                "-",
              bloodPressure:
                (labData["bps"] ||
                  labData["sys"] ||
                  record.bps ||
                  record.sys ||
                  record.systolic_bp ||
                  profile.bps ||
                  profile.sys ||
                  profile.systolic_bp) &&
                (labData["bpd"] ||
                  labData["dia"] ||
                  record.bpd ||
                  record.dia ||
                  record.diastolic_bp ||
                  profile.bpd ||
                  profile.dia ||
                  profile.diastolic_bp ||
                  profile.dias)
                  ? `${labData["bps"] || labData["sys"] || record.bps || record.sys || record.systolic_bp || profile.bps || profile.sys || profile.systolic_bp}/${labData["bpd"] || labData["dia"] || record.bpd || record.dia || record.diastolic_bp || profile.bpd || profile.dia || profile.diastolic_bp || profile.dias}`
                  : labData["sys/dias"] || "-",
              pulse: labData["pulse"] || record.pulse || profile.pulse || "-",
              doctorNote: doctorAdvice || labData["doctornote"] || "-",
              // คำแนะนำจาก AI (เหมือน Super Admin)
              nutritionAdvice: nutritionAdvice,
              exerciseAdvice: exerciseAdvice,
              sleepAdvice: sleepAdvice,
            };
          }),
        );

      // ✅ รอ templates ก่อน (โหลด parallel กับ page 1 อยู่แล้ว ใช้เวลาน้อย)
      await templatesPromise;

      // ✅ Process records ของหน้านี้ — format + match templates
      const pageRecords = deduplicateList(
        firstLabRes.result && Array.isArray(firstLabRes.list)
          ? firstLabRes.list
          : [],
      );
      if (pageRecords.length > 0) {
        const pageFormatted = await formatBatch(pageRecords);
        allFormattedData = allFormattedData.concat(pageFormatted);
        await matchTemplatesWithRecords(pageFormatted, allTemplates);
      }

      // ✅ Sort และ update state
      if (allFormattedData.length > 0) {
        sortAndUpdate(allFormattedData);
      } else {
        if (currentFetchId !== fetchIdRef.current) return;
        setHealthRecords([]);
        setAllHealthRecords([]);
        setLoading(false);
      }
    } catch (err) {
      console.error("❌ Error fetching health records:", err);
      if (currentFetchId === fetchIdRef.current) {
        setHealthRecords([]);
        setAllHealthRecords([]);
        setLoading(false);
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchHealthRecords(currentFiltersRef.current, {
      pageNow: page,
      preservePage: true,
    });
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        <PageTitle>สมุดสุขภาพ</PageTitle>

        <FilterSection>
          {/* แถวที่ 1 - Labels */}
          <GridItem column="1" row="1">
            <Label>วันที่ตรวจเริ่ม</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ถึง</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>ค้นหาชื่อ, สกุล, HN, บัตรประชาชน</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>บริษัท</Label>
          </GridItem>
          <GridItem column="5" row="1">
            <Label>สถานะ</Label>
          </GridItem>

          {/* แถวที่ 2 - Input Fields และ Buttons */}
          <GridItem column="1" row="2">
            <DateInputContainer>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="2" row="2">
            <DateInputContainer>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={startDate}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="3" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อหรือ HN"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
          </GridItem>

          <GridItem column="4" row="2">
            <SelectInput value={searchCompany} onChange={handleCompanyChange}>
              <option value="">ทั้งหมด</option>
              {companies
                .filter((company) => company.name && company.name !== "-")
                .map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
            </SelectInput>
          </GridItem>

          <GridItem column="5" row="2">
            <SelectInput
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">เลือกสถานะ</option>
              <option value="รอการอนุมัติ">รอการอนุมัติ</option>
              <option value="อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว">
                อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว
              </option>
              <option value="มีการแก้ไขรอการอนุมัติอีกครั้ง">
                มีการแก้ไขรอการอนุมัติอีกครั้ง
              </option>
            </SelectInput>
          </GridItem>

          <GridItem column="6" row="2">
            <SearchButton onClick={handleSearch}>ค้นหา</SearchButton>
          </GridItem>

          <GridItem column="7" row="2">
            <PrintButton onClick={handleBatchPrint}>
              <Printer size={16} />
              Print
            </PrintButton>
          </GridItem>
        </FilterSection>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลด...</LoadingText>
          </LoadingContainer>
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell rowSpan="2" style={{ width: "50px" }}>
                    <Checkbox
                      onChange={handleSelectAll}
                      checked={
                        healthRecords.length > 0 &&
                        healthRecords.every((record) =>
                          selectedRecords.includes(record.id),
                        )
                      }
                    />
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "50px" }}>
                    ที่
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "100px" }}>
                    วันที่ตรวจ
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "180px" }}>
                    ชื่อผู้รับการตรวจ/เพศ/อายุ/โรคประจำตัว
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "120px" }}>
                    HN
                  </TableHeaderCell>
                  <TableHeaderCell colSpan="4">ผลการตรวจสุขภาพ</TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "200px" }}>
                    การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น/ Doctor Note
                  </TableHeaderCell>
                  <TableHeaderCell rowSpan="2" style={{ width: "150px" }}>
                    การจัดการข้อมูล
                  </TableHeaderCell>
                </TableRow>
                <TableRow>
                  <TableHeaderCell2 style={{ width: "150px" }}>
                    พารามิเตอร์
                  </TableHeaderCell2>
                  <TableHeaderCell2 style={{ width: "150px" }}>
                    ค่าปกติ
                  </TableHeaderCell2>
                  <TableHeaderCell2 style={{ width: "120px" }}>
                    ค่าที่ตรวจวัดได้
                  </TableHeaderCell2>
                  <TableHeaderCell2
                    style={{ width: "50px" }}
                  ></TableHeaderCell2>
                </TableRow>
              </TableHeader>
              <tbody>
                {healthRecords.map((record, rowIndex) => (
                  <TableRow key={record.no}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleCheckboxChange(record.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {(currentPage - 1) * pageSize + rowIndex + 1}
                    </TableCell>
                    <TableCell>
                      <div>{record.date}</div>
                      {record.rawDate && (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#6B7280",
                            marginTop: "2px",
                          }}
                        >
                          {record.rawDate.split(" ")[1]?.slice(0, 8) || ""}
                        </div>
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      <div>
                        <span style={{ fontWeight: "700" }}>ชื่อ:</span>{" "}
                        <span style={{ fontWeight: "400" }}>
                          {record.patientName}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontWeight: "700" }}>เพศ:</span>{" "}
                        {record.gender}
                      </div>
                      <div>
                        <span style={{ fontWeight: "700" }}>อายุ:</span>{" "}
                        {record.age} ปี
                      </div>
                      <div>
                        <span style={{ fontWeight: "700" }}>โรคประจำตัว:</span>
                      </div>
                      <div style={{ fontWeight: "400" }}>{record.diseases}</div>
                      <div>
                        <span style={{ fontWeight: "700" }}>บริษัท:</span>{" "}
                        <span style={{ fontWeight: "400" }}>
                          {record.company}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{record.hn}</TableCell>
                    <TableCell colSpan="4" style={{ padding: 0 }}>
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <tbody>
                          {(() => {
                            const displayParameters = getUnifiedParameters(
                              record,
                              masterData,
                            );
                            const categorized =
                              categorizeParameters(displayParameters);
                            return Object.entries(categorized).map(
                              ([category, params], catIndex) => (
                                <React.Fragment key={`cat-${catIndex}`}>
                                  {/* Category Header */}
                                  <tr>
                                    <td
                                      colSpan="4"
                                      style={{
                                        padding: "0.5rem 0 0.25rem 0",
                                        fontWeight: "700",
                                        fontSize: "13px",
                                        color: "#4a4a4a",
                                        textAlign: "left",
                                        borderTop:
                                          catIndex > 0
                                            ? "1px solid #e0e0e0"
                                            : "none",
                                      }}
                                    >
                                      {category}
                                    </td>
                                  </tr>
                                  {/* Parameters in this category */}
                                  {params.map((param, index) => {
                                    const rawComparison = compareValues(
                                      param.normal,
                                      param.result,
                                      param.name,
                                    );
                                    const isTextCategory =
                                      category === "ผลการตรวจปัสสาวะ" ||
                                      category === "ผลการตรวจอุจจาระ";
                                    const isTextBasedRange = param.normal
                                      ? /[a-zA-Z]/.test(
                                          String(param.normal).trim(),
                                        ) &&
                                        !/^[MF]\s/.test(
                                          String(param.normal).trim(),
                                        )
                                      : false;
                                    const isStoolFoundParam =
                                      category === "ผลการตรวจอุจจาระ" &&
                                      (param.name === "Stool Color" ||
                                        param.name === "Color" ||
                                        param.name === "Stool WBC" ||
                                        param.name === "WBC" ||
                                        param.name === "Stool RBC" ||
                                        param.name === "RBC" ||
                                        param.name ===
                                          "Hemoglobin detection stool" ||
                                        param.name === "Parasite");
                                    const isNormalAbnormalParam =
                                      param.name === "Anti-HBs Concentration";
                                    const comparison =
                                      param.isHepatitis &&
                                      rawComparison.label !== "-" &&
                                      isTextBasedRange
                                        ? {
                                            status:
                                              rawComparison.status === "normal"
                                                ? "normal"
                                                : "high",
                                            label:
                                              rawComparison.status === "normal"
                                                ? "Negative"
                                                : "Positive",
                                          }
                                        : isNormalAbnormalParam &&
                                            rawComparison.label !== "-"
                                          ? {
                                              status:
                                                rawComparison.status ===
                                                "normal"
                                                  ? "normal"
                                                  : "high",
                                              label:
                                                rawComparison.status ===
                                                "normal"
                                                  ? "Normal"
                                                  : "Abnormal",
                                            }
                                          : isStoolFoundParam &&
                                              rawComparison.label !== "-"
                                            ? {
                                                status: rawComparison.status,
                                                label:
                                                  rawComparison.status ===
                                                  "normal"
                                                    ? "Not Found"
                                                    : "Found",
                                              }
                                            : isTextCategory &&
                                                rawComparison.label !== "-" &&
                                                rawComparison.status !==
                                                  "normal"
                                              ? {
                                                  status: "high",
                                                  label: "Abnormal",
                                                }
                                              : rawComparison;
                                    return (
                                      <tr key={`${catIndex}-${index}`}>
                                        <td
                                          style={{
                                            padding: "0.125rem 1rem",
                                            textAlign: "left",
                                            fontSize: "12px",
                                            color: "#6F6F6F",
                                            width: "150px",
                                          }}
                                        >
                                          {param.name}
                                        </td>
                                        <td
                                          style={{
                                            padding: "0.125rem 1rem",
                                            textAlign: "center",
                                            fontSize: "12px",
                                            color: "#6F6F6F",
                                            whiteSpace: "nowrap",
                                            width: "150px",
                                          }}
                                        >
                                          {param.normal}
                                        </td>
                                        <td
                                          style={{
                                            padding: "0.125rem 0.5rem",
                                            textAlign: "center",
                                            fontSize: "12px",
                                            color: "#6F6F6F",
                                            width: "120px",
                                          }}
                                        >
                                          {param.result}
                                        </td>
                                        <td
                                          style={{
                                            padding: "0.125rem 0.25rem",
                                            textAlign: "left",
                                            fontSize: "12px",
                                            color: "#6F6F6F",
                                            width: "50px",
                                          }}
                                        >
                                          <ComparisonBadge
                                            status={comparison.status}
                                          >
                                            {comparison.label}
                                          </ComparisonBadge>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              ),
                            );
                          })()}
                        </tbody>
                      </table>
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      {/* แสดง Templates */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.25rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {record.templates.map((templateName, index) => {
                          // หา matched template data ที่ตรงกับ template name นี้
                          const matchedTemplate = record.matchedTemplates?.find(
                            (t) => t.template_name === templateName,
                          );

                          // ✅ ปิดการแก้ไขเฉพาะเมื่อสถานะเป็น 'approved' เท่านั้น
                          const isDisabled =
                            record.approvalStatus === "approved";

                          return (
                            <TemplateBadge
                              key={index}
                              disabled={isDisabled}
                              onClick={() =>
                                handleTemplateClick(
                                  templateName,
                                  matchedTemplate,
                                )
                              }
                            >
                              <TemplateBadgeText>
                                {templateName}
                              </TemplateBadgeText>
                              {!isDisabled && (
                                <RemoveButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTemplate(record.id, index);
                                  }}
                                  title="ลบ template"
                                >
                                  <X size={8} />
                                </RemoveButton>
                              )}
                            </TemplateBadge>
                          );
                        })}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.25rem",
                        }}
                      >
                        <div style={{ display: "flex", gap: "0.25rem" }}>
                          {/* ✅ ปุ่ม "แก้ไข" - ปิดการใช้งานเฉพาะเมื่อสถานะเป็น 'approved' */}
                          <EditButton
                            disabled={record.approvalStatus === "approved"}
                            onClick={() =>
                              record.approvalStatus !== "approved" &&
                              handleEdit(record.id)
                            }
                          >
                            แก้ไข
                          </EditButton>
                          {/* ✅ ปุ่ม "ส่งใหม่" - แสดงเฉพาะเมื่อสถานะเป็น 'approved' */}
                          {/* ✅ ปุ่ม "กดเพื่อส่งผลสุขภาพ" - แสดงเมื่อสถานะเป็น 'pending' หรือ 'revised' */}
                          {record.approvalStatus === "approved" ? (
                            <ResendButton
                              onClick={() => handleSendResult(record.id)}
                            >
                              ส่งใหม่
                            </ResendButton>
                          ) : (
                            <SendResultButton
                              onClick={() => handleSendResult(record.id)}
                            >
                              กดเพื่อส่งผลสุขภาพ
                            </SendResultButton>
                          )}
                        </div>
                        {/* ✅ ปุ่ม "ประวัติการส่งผลสุขภาพ" - แสดงเมื่อสถานะเป็น approved หรือ revised */}
                        {(record.approvalStatus === "approved" ||
                          record.approvalStatus === "revised") && (
                          <HistoryButton
                            onClick={() => handleViewHistory(record.id)}
                          >
                            <History />
                            ประวัติการส่งผลสุขภาพ
                          </HistoryButton>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          alignItems: "center",
                        }}
                      >
                        <ViewDataButton
                          onClick={() => handleViewData(record.id)}
                        >
                          ดูข้อมูล
                        </ViewDataButton>
                        <StatusText>สถานะจัดส่ง</StatusText>
                        <StatusBadge
                          $status={
                            record.approvalStatus === "approved"
                              ? "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
                              : record.approvalStatus === "revised"
                                ? "มีการแก้ไขรอการอนุมัติอีกครั้ง"
                                : "รอการอนุมัติ"
                          }
                        >
                          {record.approvalStatus === "approved"
                            ? "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
                            : record.approvalStatus === "revised"
                              ? "มีการแก้ไขรอการอนุมัติอีกครั้ง"
                              : "รอการอนุมัติ"}
                        </StatusBadge>
                        <ActionPrintButton
                          onClick={() => handlePrint(record.id)}
                        >
                          <Printer size={14} />
                          Print
                        </ActionPrintButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && healthRecords.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan="20"
                      style={{
                        textAlign: "center",
                        color: "#9ca3af",
                        padding: "2rem",
                      }}
                    >
                      ไม่พบรายการ
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>

            {/* Pagination */}
            {totalPages > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  หน้า {currentPage} จาก {totalPages} หน้า
                </PaginationInfo>
                <PaginationControls>
                  <PageButton
                    onClick={() =>
                      handlePageChange(Math.max(1, currentPage - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    ก่อนหน้า
                  </PageButton>

                  {(() => {
                    const pages = [];

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(
                          <PageButton
                            key={i}
                            active={currentPage === i}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </PageButton>,
                        );
                      }
                    } else {
                      pages.push(
                        <PageButton
                          key={1}
                          active={currentPage === 1}
                          onClick={() => handlePageChange(1)}
                        >
                          1
                        </PageButton>,
                      );

                      if (currentPage > 3) {
                        pages.push(
                          <PageEllipsis key="ellipsis1">...</PageEllipsis>,
                        );
                      }

                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <PageButton
                            key={i}
                            active={currentPage === i}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </PageButton>,
                        );
                      }

                      if (currentPage < totalPages - 2) {
                        pages.push(
                          <PageEllipsis key="ellipsis2">...</PageEllipsis>,
                        );
                      }

                      pages.push(
                        <PageButton
                          key={totalPages}
                          active={currentPage === totalPages}
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                        </PageButton>,
                      );
                    }

                    return pages;
                  })()}

                  <PageButton
                    onClick={() =>
                      handlePageChange(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    ถัดไป
                  </PageButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </TableContainer>
        )}
      </PageContainer>

      <TemplateDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateData={selectedTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        recordId={selectedRecordId}
        selectedRecord={selectedRecord}
        onSave={() => {
          const currentFilters = {};
          if (startDate)
            currentFilters.dateStart = formatDateToYYYYMMDD(startDate);
          if (endDate)
            currentFilters.dateStop = formatDateStopToYYYYMMDD(endDate);
          fetchHealthRecords(currentFilters, {
            pageNow: currentPage,
            preservePage: true,
          });
        }}
      />

      <HealthResultModal
        isOpen={isHealthResultModalOpen}
        onClose={handleCloseHealthResultModal}
        selectedRecord={selectedRecord}
        onApprove={handleApprove}
      />

      <SendHealthResultModal
        isOpen={isSendHealthResultModalOpen}
        onClose={handleCloseSendHealthResultModal}
        onConfirm={handleConfirmSendAgain}
      />

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedReportRecord}
      />

      {/* Hidden Templates Modal */}
      {isHiddenTemplatesModalOpen && (
        <HiddenTemplatesModal
          isOpen={isHiddenTemplatesModalOpen}
          onClose={() => setIsHiddenTemplatesModalOpen(false)}
          hiddenTemplates={hiddenTemplates}
          onRestore={handleRestoreTemplate}
        />
      )}

      {/* Alert Modal */}
      {isAlertModalOpen && (
        <AlertModalOverlay onClick={() => setIsAlertModalOpen(false)}>
          <AlertModalContainer onClick={(e) => e.stopPropagation()}>
            <AlertModalBody>
              กรุณาเลือกผู้รับการตรวจที่ต้องการ Print
            </AlertModalBody>
            <AlertModalButton onClick={() => setIsAlertModalOpen(false)}>
              ตกลง
            </AlertModalButton>
          </AlertModalContainer>
        </AlertModalOverlay>
      )}

      {/* History Modal */}
      <HealthRecordsHistory
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        selectedRecord={historySelectedRecord}
      />
    </Layout>
  );
};

// Hidden Templates Modal Component
const HiddenTemplatesModal = ({
  isOpen,
  onClose,
  hiddenTemplates,
  onRestore,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Templates ที่ซ่อนไว้</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {hiddenTemplates.length === 0 ? (
            <EmptyMessage>ไม่มี Templates ที่ซ่อนไว้</EmptyMessage>
          ) : (
            <HiddenTemplatesList>
              {hiddenTemplates.map((template) => (
                <HiddenTemplateItem key={template.template_id}>
                  <TemplateInfo>
                    <TemplateName>{template.template_name}</TemplateName>
                    <TemplateDetails>
                      <div>
                        ซ่อนเมื่อ:{" "}
                        {new Date(template.hidden_at).toLocaleString("th-TH")}
                      </div>
                      {template.tags && template.tags.length > 0 && (
                        <TagsContainer>
                          {template.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      )}
                    </TemplateDetails>
                  </TemplateInfo>
                  <RestoreButton
                    onClick={() => onRestore(template.template_id)}
                  >
                    เอากลับมา
                  </RestoreButton>
                </HiddenTemplateItem>
              ))}
            </HiddenTemplatesList>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 2rem;
`;

const HiddenTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HiddenTemplateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: #f9fafb;
`;

const TemplateInfo = styled.div`
  flex: 1;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const TemplateDetails = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background-color: #e0f2f1;
  color: #00796b;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RestoreButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #009fa2;
  }
`;

// Alert Modal Styles
const AlertModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const AlertModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 450px;
  max-width: 90vw;
  padding: 3rem 2rem;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const AlertModalBody = styled.div`
  color: #111827;
  font-size: 1.25rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.6;
`;

const AlertModalButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 100px;

  &:hover {
    background-color: #009fa2;
  }
`;

export default HealthBook;
